import React from "react";
import {
  SpreadsheetTableColumn,
  SpreadsheetTableColumnId,
  SpreadsheetTableDefaultDataItem,
  SpreadsheetTableProps,
  SpreadsheetTableSelectionState,
  SpreadsheetTableCellSelection,
  SpreadsheetTableApiResponse,
  SpreadsheetTableColumnResult,
  SpreadsheetTableQueryModel,
  SPREADSHEET_TABLE_CONSTANTS,
  SpreadsheetTableWhereClause,
  SpreadsheetTableRunPayload,
  SpreadsheetTableColumnName,
  SpreadsheetTableColumnFilterOption,
} from "./table.types";
import {
  copyToClipboard,
  formatSpreadsheetDataForClipboard,
  isCopyKeyCombination,
  isCutKeyCombination,
  isPasteKeyCombination,
  isSelectAllKeyCombination,
  isUndoKeyCombination,
  isRedoKeyCombination,
  readFromClipboard,
  parseClipboardDataForPaste,
  parseClipboardDataAsMatrix,
  isTabularClipboardData,
} from "./utils/clipboard.utils";
import {
  createSelection,
  createSelectionState,
  isCellInSelection,
  updateSelection,
  createRowSelection,
  createRowRangeSelection,
  createRowSelectionState,
  selectSingleRow,
  toggleRowSelection,
  selectRowRange,
  clearRowSelection,
  isRowSelected,
  getSelectedRowIndices,
  RowSelectionState,
} from "./utils/selection.utils";
import {
  fetchSpreadsheetColumns,
  fetchSpreadsheetData,
  fetchPedigreeSpreadsheetData,
  loadIndividualSpreadsheetFormat,
  loadPedigreeSpreadsheetFormat,
  updateCellValue,
} from "@common/actions/table";
import { saveFormat } from "@modules/spreadsheets/actions/spreadsheet";
import {
  CellData,
  CellUpdatePayload,
  SaveFormatPayload,
  SmartColumn,
  SpreadsheetType,
} from "@modules/spreadsheets/dto/spreadsheet/type";
import { debounce } from "@common/utils/common";
import { getFilterOptions } from "@modules/spreadsheets/pages/spreadsheet-details/components/spreadsheet-result/components/table/filter-utils";
import { getFolders } from "@common/actions/folders";
import { SPREADSHEET_TYPE } from "@modules/spreadsheets/const/common";
import { openDatasheetFromIndividual, openPedigreeFromIndividual } from "@common/actions/individuals";
import { openPedigreeDatasheet, openPedigreeFromPedigrees } from "@modules/pedigrees/services/pedigrees";
import { HttpErrorDto } from "@common/dto/http";
import { ensureRowCountColumn } from "./utils/column.utils";
import { I18n } from "react-redux-i18n";
import { computeQueryModel } from "./query.helpers";
import { normalizeLookupValues, cleanSmartColumnsForApi, cleanQueryModelForApi } from "./data-converter";
import { getActiveSpreadsheetId, setActiveSpreadsheetId } from "./active-spreadsheet.util";
import { storeFilterData, updateFormatData } from "@modules/spreadsheets/actions/spreadsheetFormat";
import { validateSpreadsheetDateInput, getSpreadsheetInvalidDateMessage } from "@common/utils/dateValidation";

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Normalize API response into a standard 2D cell matrix.
 * Handles variations in response shape from different endpoints.
 */
const normalizeCellMatrix = (
  res: SpreadsheetTableApiResponse
): CellData[][] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  //if (Array.isArray(res?.entity?.data)) return res.entity.data;
  console.warn("Unexpected ISS load response shape:", res);
  return [];
};
export const mapApiDataToRows = (
  cellMatrix: CellData[][],
  startAt = 0,
  isRunPayload = false,
  rowData?: Array<{ globalid?: number; id?: number }>,
  fieldContext?: number
): Array<SpreadsheetTableDefaultDataItem> => {
  if (!Array.isArray(cellMatrix)) return [];
  return cellMatrix.map((cells: CellData[], rowIdx: number) => {
    const row: SpreadsheetTableDefaultDataItem = {
      id: String(startAt + rowIdx),
      rowCount: startAt + rowIdx + 1,
    };
    // Add Sr No column for runPayload scenarios
    if (isRunPayload) {
      row.srNo = startAt + rowIdx + 1;
    }
    // Add globalId and fieldContext for API calls
    if (rowData && rowData[rowIdx]) {
      // For individual spreadsheets: use globalid
      // For pedigree spreadsheets: use id (family/pedigree ID)
      const isPedigreeContext = fieldContext === 2;
      row.globalId = isPedigreeContext
        ? rowData[rowIdx]?.id
        : rowData[rowIdx]?.globalid ?? rowData[rowIdx]?.id;
    }
    if (fieldContext !== undefined) {
      row.fieldContext = fieldContext;
    }
    for (let cellIndex = 0; cellIndex < cells.length; cellIndex++) {
      const cell = cells[cellIndex];
      const fieldId = cell?.field?.fieldid;
      const fieldName = cell?.field?.fieldname as string | undefined;

      // Skip special/system fields
      if (fieldId === SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID) continue;
      if (
        !fieldName ||
        SPREADSHEET_TABLE_CONSTANTS.EXCLUDED_FIELD_NAMES.includes(
          fieldName.trim().toLowerCase()
        )
      )
        continue;
      let accessorKey = `${(fieldName || "").trim()}__f${fieldId}`
      if (cell?.tableId && cell?.tableId > 0) {
        accessorKey = `${cell.tableId}__${(fieldName || "").trim()}__f${fieldId}`
      }
      const key = accessorKey;
      row[key] = cell?.dataValue ?? cell?.rawValue ?? "";

      if (cell?.displayValue !== undefined && cell?.displayValue !== null) {
        row[`${key}_display`] = cell.displayValue;
      }

      // Store cell-specific permissions from cell.field.permissions
      row[`${key}_permissions`] = cell?.field?.permissions ;

      if (cell && "rrn" in cell && typeof cell.rrn === "number") {
        row.rrn = cell.rrn;
      }
    }
    return row;
  });
};

/**
 * Helper function to check if a field name should be excluded
 * This avoids the TypeScript type issue with the includes method
 */
const isExcludedFieldName = (fieldName: string): boolean => {
  const normalizedName = fieldName.trim().toLowerCase();
  return SPREADSHEET_TABLE_CONSTANTS.EXCLUDED_FIELD_NAMES.includes(
    normalizedName as string
  );
};

// Old query helpers removed in favor of centralized computeQueryModel utility

// =============================================================================
// COLUMN MAPPING FUNCTIONS
// =============================================================================

/**
 * Convert saved spreadsheet columns to table column configuration.
 * Used for scenarios 1 & 3 (existing spreadsheets and save+run).
 */
const mapApiColumnsToTableColumns = (
  apiColumns: SmartColumn[]
): SpreadsheetTableColumn[] => {
  const result = apiColumns
    .filter((col) => {
      // Safety check: ensure col.field exists before accessing field id
      if (!col.field) {
        return false;
      }

      const isRowCount =
        col.field.fieldid === SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID;
      const name = col.field.fieldname?.trim();
      return isRowCount || (name && !isExcludedFieldName(name));
    })
    .map((col, idx) => {
      // Safety check: ensure col.field exists
      if (!col.field) {
        return null;
      }

      // Handle row count column
      if (
        col.field.fieldid === SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID
      ) {
        return {
          id: SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID,
          header: "",
          type: "static",
          accessorKey: "rowCount",
          enableResizing: false,
          size: col.width ?? SPREADSHEET_TABLE_CONSTANTS.MIN_COLUMN_WIDTH,
          minSize: SPREADSHEET_TABLE_CONSTANTS.MIN_COLUMN_WIDTH,
          maxSize: SPREADSHEET_TABLE_CONSTANTS.MIN_COLUMN_WIDTH,
          editable: false,
          colName: "srNo",
        };
      }

      // Extract field metadata
      const fieldName = col.field.fieldname?.trim();
      const parentFieldName = col.parentField?.fieldname?.trim();
      const fieldType = col.field.fieldtype?.typename || "Unknown";
      const fieldId = col.field.fieldid;
      const isSystem = col.field.system || false;
      const parentField = col?.parentField;
      let lookupValues = col?.field.lookupValues;
      lookupValues = normalizeLookupValues(lookupValues);

      // Generate filter options
      const filterOptions = getFilterOptions({
        fieldId,
        fieldName,
        fieldType,
        isSystem,
        dropdownValues: col.field.dropdownValues,
        colName: fieldName,
        typeId: col.field.fieldtype?.typeid,
        tableIndex: col.field.tableIndex,
        fieldClass: col.field.fieldClass,
        lookupValues: col.field?.lookupValues || {},
        isRunPayload: false,
      });

      // Check if this is a folder path field
      const isFolderPathField =
        SPREADSHEET_TABLE_CONSTANTS.FOLDER_PATH_FIELD_NAMES.includes(
          fieldName as "Folder Path"
        ) ||
        SPREADSHEET_TABLE_CONSTANTS.FOLDER_PATH_FIELD_IDS.includes(
          fieldId as 73
        );
      
      let accessorKey = `${fieldName}__f${fieldId}`
      
      if (parentField && parentField.fieldid) {
        accessorKey = `${parentField.fieldid}__${fieldName}__f${fieldId}`
      }

      // Build column configuration
      return {
        id: fieldId,
        header: parentFieldName ? `${parentFieldName}.${fieldName}` : fieldName,
        colName: fieldName,
        editable: col.editable ?? false,
        securityEditable: true,
        accessorKey: accessorKey,
        enableResizing: true,
        size: col.width || SPREADSHEET_TABLE_CONSTANTS.DEFAULT_COLUMN_WIDTH,
        minSize: SPREADSHEET_TABLE_CONSTANTS.MIN_COLUMN_WIDTH,
        maxSize: SPREADSHEET_TABLE_CONSTANTS.MAX_COLUMN_WIDTH,
        type: "text",
        typeId: col.field.fieldtype?.typeid,
        system: isSystem,
        tableIndex: col.field.tableIndex,
        fieldClass: col.field.fieldClass,
        sortOrder: col.sortOrder,
        sortAsc: col.sortAsc,
        modulo: col.modulo,
        parentFieldName: parentFieldName,
        parentField: parentField,
        fieldType: fieldType,
        dropdownOptions: filterOptions.options,
        lookupValues: lookupValues,
        dataDropdownOptions: col.field.dropdownValues?.map((value: string) => ({
          actualValue: value,
          displayValue: value,
        })),
        // Special handling for Folder Path field
        filter: isFolderPathField
          ? {
              type: 31, // folder
              getFolders: () => getFolders({ context: 2 }),
            }
          : {
              type: filterOptions.type,
              options: filterOptions.options,
              lookupValues: filterOptions.lookupValues,
            },
      };
    })
    .filter(Boolean) as SpreadsheetTableColumn[]; // Remove any null entries
  
  return result;
};

/**
 * Convert runPayload columns to table column configuration.
 * Used for scenario 2 (run only from management).
 * Adds Sr No column and handles different field structure.
 */
const mapRunPayloadColumnsToTableColumns = (
  runPayloadColumns: SmartColumn[]
): SpreadsheetTableColumn[] => {
  // Create Sr No column for runPayload scenarios
  const srNoColumn: SpreadsheetTableColumn = {
    id: SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID,
    header: "",
    colName: "srNo",
    accessorKey: "rowCount",
    enableResizing: false,
    size: 50,
    minSize: 50,
    maxSize: 50,
    type: "static",
    sortOrder: 0,
    sortAsc: false,
    editable: false,
    filter: {
      type: 1, // text
      options: [],
    },
  };

  const mappedColumns = runPayloadColumns
    .filter((col) => {
      const isVisible = col.visible !== false;
      const hasValidField =
        col.field && col.field.fieldid && col.field.fieldname;
      const isRowCount =
        col.field?.fieldid === SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID;
      return isVisible && (hasValidField || isRowCount);
    })
    .map((col, idx) => {
      // Safety check: ensure col.field exists
      if (!col.field) {
        return null;
      }

      // Handle row count column
      if (
        col.field.fieldid === SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID
      ) {
        return {
          id: SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID,
          header: "",
          type: "static",
          accessorKey: "srNo",
          enableResizing: false,
          size: col.width ?? SPREADSHEET_TABLE_CONSTANTS.MIN_COLUMN_WIDTH,
          minSize: SPREADSHEET_TABLE_CONSTANTS.MIN_COLUMN_WIDTH,
          maxSize: SPREADSHEET_TABLE_CONSTANTS.MIN_COLUMN_WIDTH,
          editable: false,
          colName: "srNo",
        };
      }

      // Extract field metadata from runPayload structure
      const fieldName = col.field.fieldname?.trim();
      const fieldId = col.field.fieldid;
      const isSystem = col.field.system || false;
      const fieldType = col.field.type;
      const parentField = col?.parentField;
      const parentFieldName = parentField?.fieldname?.trim();
      let lookupValues = col?.field.lookupValues;

      lookupValues = normalizeLookupValues(lookupValues);


      let dropdownOptionsModified: Array<SpreadsheetTableColumnFilterOption> | string[] | undefined = col.field.dropdownValues
      if (dropdownOptionsModified && dropdownOptionsModified.length > 0) {
        const isAlreadyTransformed = dropdownOptionsModified[0] &&
          typeof dropdownOptionsModified[0] === 'object' &&
          dropdownOptionsModified[0] !== null &&
          'actualValue' in dropdownOptionsModified[0] &&
          'displayValue' in dropdownOptionsModified[0];
        
        if (!isAlreadyTransformed) {
          const transformedDropdowns = col.field.dropdownValues?.map((value: string) => ({
            actualValue: value,
            displayValue: value,
          }))
          dropdownOptionsModified = transformedDropdowns
        }
      }

      // Generate filter options with field options from columnsTableFields
      const filterOptions = getFilterOptions({
        fieldId,
        fieldName,
        fieldType: fieldType?.toString(),
        isSystem,
        dropdownValues: dropdownOptionsModified,
        colName: fieldName,
        typeId: fieldType,
        tableIndex: col.tableIndex,
        fieldClass: col.fieldClass,
        lookupValues: col.field?.lookupValues || {},
        isRunPayload: true,
      });

      // Check if this is a folder path field
      const isFolderPathField =
        SPREADSHEET_TABLE_CONSTANTS.FOLDER_PATH_FIELD_NAMES.includes(
          fieldName as "Folder Path"
        ) ||
        SPREADSHEET_TABLE_CONSTANTS.FOLDER_PATH_FIELD_IDS.includes(
          fieldId as 73
        );
      
      let accessorKey = `${fieldName}__f${fieldId}`

      if (parentField && parentField.fieldid) {
        accessorKey = `${parentField.fieldid}__${fieldName}__f${fieldId}`
      }

      // Build column configuration for runPayload
      return {
        id: fieldId,
        header: parentFieldName ? `${parentFieldName}.${fieldName}` : fieldName,
        typeId:  fieldType,
        colName: fieldName,
        accessorKey: accessorKey,
        enableResizing: true,
        size: col.width || SPREADSHEET_TABLE_CONSTANTS.DEFAULT_COLUMN_WIDTH,
        minSize: SPREADSHEET_TABLE_CONSTANTS.MIN_COLUMN_WIDTH,
        maxSize: SPREADSHEET_TABLE_CONSTANTS.MAX_COLUMN_WIDTH,
        type: "text",
        sortOrder: col.sortOrder || 0,
        sortAsc: col.sortAsc || false,
        editable: col.editable,
        system: isSystem,
        dropdownOptions: filterOptions.options,
        lookupValues: lookupValues,
        dataDropdownOptions: dropdownOptionsModified,
        parentField: parentField,
        modulo: col.modulo,
        parentFieldName: parentField?.fieldname,
        filter: isFolderPathField
          ? {
              type: 31, // folder
              getFolders: () => getFolders({ context: 2 }),
            }
          : {
              type: filterOptions.type,
              options: filterOptions.options,
              lookupValues: filterOptions.lookupValues,
            },
      };
    })
    .filter(Boolean) as SpreadsheetTableColumn[]; // Remove any null entries

  return [srNoColumn, ...mappedColumns];
};

// =============================================================================
// PAYLOAD CREATION FUNCTIONS
// =============================================================================

/**
 * Create request payload for saved spreadsheet data (scenarios 1 & 3).
 * Strips dropdown values to keep payload lighter.
 */
const createDataPayload = (
  columnModel: { smartColumns: SmartColumn[]; columnCount: number },
  opts?: {
    startAt?: number;
    size?: number;
    scroll?: boolean;
    query?: SpreadsheetTableQueryModel;
    filter?: { whereClauses: SpreadsheetTableWhereClause[]; andOR?: number };
    folderId?: number;
    familyId?: number;
  }
) => {
  // Safety check for columnModel
  if (!columnModel || !columnModel.smartColumns) {
    console.error("createDataPayload: columnModel or smartColumns is undefined", columnModel);
    throw new Error("Column model is not available for data loading");
  }

  return {
    folderId: opts?.folderId ?? 0,
    familyId: opts?.familyId ?? 0,
    startAt: opts?.startAt ?? 0,
    size: opts?.size ?? SPREADSHEET_TABLE_CONSTANTS.DEFAULT_PAGE_SIZE,
    scroll: opts?.scroll ?? false,
    columnModel: {
      smartColumns: cleanSmartColumnsForApi(columnModel.smartColumns),
    },
    queryModel: opts?.query ? cleanQueryModelForApi(opts.query) : { whereClauses: [] },
    filter: opts?.filter ?? { whereClauses: [] },
  };
};

/**
 * Create payload for loadISS API when using runPayload (scenario 2: run only).
 * Passes runPayload structure directly to loadISS endpoint.
 */
const createRunPayloadDataPayload = (
  runPayload: SpreadsheetTableRunPayload,
  opts?: {
    startAt?: number;
    size?: number;
    scroll?: boolean;
    query?: SpreadsheetTableQueryModel;
    filter?: { whereClauses: SpreadsheetTableWhereClause[]; andOR?: number };
  }
) => {
  return {
    folderId: runPayload.folderId ?? runPayload.ssFormat?.folderId ?? 0,
    familyId: runPayload.familyId ?? 0,
    startAt: opts?.startAt ?? 0,
    size: opts?.size ?? SPREADSHEET_TABLE_CONSTANTS.DEFAULT_PAGE_SIZE,
    scroll: opts?.scroll ?? true,
    columnModel: {
      smartColumns: cleanSmartColumnsForApi(runPayload.columnModel.smartColumns),
    },
    queryModel: opts?.query 
      ? cleanQueryModelForApi(opts.query)
      : runPayload.queryModel 
        ? cleanQueryModelForApi(runPayload.queryModel)
        : { whereClauses: [] },
    filter: opts?.filter ?? { whereClauses: [] },
  };
};

// =============================================================================
// MAIN CONTROLLER HOOK
// =============================================================================

// ===== Active spreadsheet tracking delegated to utility for easy removal =====

// Helper to get dispatch from store (for react-redux v5 compatibility)
const getDispatch = () => {
    // Try to get dispatch from window store (exposed in index.js)
    if (typeof window !== 'undefined' && (window as any).__SPREADSHEET_STORE__) {
        return (window as any).__SPREADSHEET_STORE__.dispatch;
    }
    // Fallback: try to get from React context (if available)
    // This is a workaround for react-redux v5 which doesn't export useDispatch
    throw new Error('Redux store not found. Make sure the spreadsheet module is initialized.');
};

const getFilterFromRedux = (spreadsheetId: number | string | undefined,spreadsheetTypeName: SpreadsheetType|string) => {
    if (spreadsheetId == null || spreadsheetId === undefined || typeof window === 'undefined' || !(window as any).__SPREADSHEET_STORE__) {
        return undefined;
    }
    try {
        const store = (window as any).__SPREADSHEET_STORE__;
        const state = store.getState();
        const spreadsheetType = spreadsheetTypeName === SpreadsheetType.individual ? 'Individual' : 'Pedigree';
        const typeData = state?.spreadsheetFormat?.[spreadsheetType] || {};
        const storedData = typeData[Number(spreadsheetId)];
        if (storedData && storedData.filter && Array.isArray(storedData.filter.whereClauses) && storedData.filter.whereClauses.length > 0) {
            return storedData.filter;
        }
    } catch (e) {
        console.warn('Could not retrieve filter from Redux:', e);
    }
    return undefined;
};

// Helper to get format data (columnModel, queryModel, format) from Redux
const getFormatDataFromRedux = (spreadsheetId: number | string | undefined, spreadsheetTypeName: SpreadsheetType | string) => {
    if (spreadsheetId == null || spreadsheetId === undefined || typeof window === 'undefined' || !(window as any).__SPREADSHEET_STORE__) {
        return null;
    }
    try {
        const store = (window as any).__SPREADSHEET_STORE__;
        const state = store.getState();
        const spreadsheetType = spreadsheetTypeName === SpreadsheetType.individual ? 'Individual' : 'Pedigree';
        const typeData = state?.spreadsheetFormat?.[spreadsheetType] || {};
        const storedData = typeData[Number(spreadsheetId)];
        if (storedData && storedData.columnModel && storedData.columnModel.smartColumns) {
            return {
                columnModel: storedData.columnModel,
                queryModel: storedData.queryModel || { whereClauses: [] },
                formatDetails: storedData.format || undefined
            };
        }
    } catch (e) {
        console.warn('Could not retrieve format data from Redux:', e);
    }
    return null;
};

// Helper to store format data in Redux
const storeFormatDataInRedux = (spreadsheetId: number, spreadsheetTypeName: SpreadsheetType | string, formatData: any) => {
    if (typeof window === 'undefined' || !(window as any).__SPREADSHEET_STORE__) {
        return;
    }
    try {
        const store = (window as any).__SPREADSHEET_STORE__;
        const { storeFormatData } = require('@modules/spreadsheets/actions/spreadsheetFormat');
        const spreadsheetType = spreadsheetTypeName === SpreadsheetType.individual ? 'Individual' : 'Pedigree';
        
        // Deep clone queryModel to preserve all fields including andOr in clauses
        const queryModel = formatData.queryModel 
            ? JSON.parse(JSON.stringify(formatData.queryModel))
            : { whereClauses: [] };
        
        const dataToStore = {
            format: formatData.formatDetails ? {
                id: Number(spreadsheetId),
                name: formatData.formatDetails.name || '',
                description: formatData.formatDetails.description || '',
                folderId: formatData.formatDetails.folderId || 0
            } : undefined,
            columnModel: formatData.columnModel || { smartColumns: [] },
            queryModel: queryModel,
            timestamp: Date.now()
        };
        
        store.dispatch(storeFormatData(spreadsheetId, spreadsheetType, dataToStore));
    } catch (e) {
        console.warn('Could not store format data in Redux:', e);
    }
};

export const useController = (props: SpreadsheetTableProps) => {
  // Get dispatch from store (react-redux v5 doesn't support useDispatch hook)
  const dispatch = React.useMemo(() => {
    try {
      return getDispatch();
    } catch (e) {
      // Return a no-op function if store is not available
      return () => {};
    }
  }, []);

  const [filterVersion, setFilterVersion] = React.useState(0);

  const [data, setData] = React.useState<
    Array<SpreadsheetTableDefaultDataItem>
  >([]);
  const [dataResult, setDataResult] =
    React.useState<SpreadsheetTableApiResponse>();
  const [columns, setColumns] = React.useState<SpreadsheetTableColumn[]>([]);
  const [smartColumns, setSmartColumns] = React.useState<SmartColumn[]>([]);
  const [selectionState, setSelectionState] =
    React.useState<SpreadsheetTableSelectionState>(createSelectionState());
  const [selectedColumnIndex, setSelectedColumnIndex] = React.useState<
    number | null
  >(null);
  const [rowSelectionState, setRowSelectionState] =
    React.useState<RowSelectionState>(createRowSelectionState());
  const [cellToUpdate, setCellToUpdate] = React.useState<{
    rowIndex: number;
    columnId: number;
    newValue: string | number;
  } | null>(null);
  const [currentlyEditingCell, setCurrentlyEditingCell] = React.useState<{
    rowIndex: number;
    columnId: number;
  } | null>(null);

  // Use refs to access current values in callbacks (optimized single ref object)
  const stateRef = React.useRef({
    columns: [] as SpreadsheetTableColumn[],
    data: [] as Array<SpreadsheetTableDefaultDataItem>,
    dataResult: undefined as SpreadsheetTableApiResponse | undefined,
    smartColumns: [] as SmartColumn[],
    selectionState: undefined as SpreadsheetTableSelectionState | undefined,
    selectedColumnIndex: null as number | null,
    rowSelectionState: undefined as RowSelectionState | undefined,
  });

  // Update refs when state changes (single effect for better performance)
  React.useEffect(() => {
    stateRef.current.columns = columns;
    stateRef.current.data = data;
    stateRef.current.dataResult = dataResult;
    stateRef.current.smartColumns = smartColumns;
    stateRef.current.selectionState = selectionState;
    stateRef.current.selectedColumnIndex = selectedColumnIndex;
    stateRef.current.rowSelectionState = rowSelectionState;
  }, [
    columns,
    data,
    dataResult,
    smartColumns,
    selectionState,
    selectedColumnIndex,
    rowSelectionState,
  ]);

  // Use undo/redo functions from parent props
  const {
    trackCellEdit: parentTrackCellEdit,
    trackRowAdd: parentTrackRowAdd,
    trackRowDelete: parentTrackRowDelete,
  } = props;

  const [columnsWidth, setColumnsWidth] = React.useState<
    Record<SpreadsheetTableColumnId, number>
  >({});
  const [internalClipboard, setInternalClipboard] = React.useState<string>("");
  const [contextMenu, setContextMenu] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const [contextMenuCell, setContextMenuCell] = React.useState<{
    rowIndex: number;
    columnId: SpreadsheetTableColumnId;
  } | null>(null);

  // Preserve selection state for context menu operations
  const preservedSelectionRef =
    React.useRef<typeof selectionState.selection>(null);
  const [startAt, setStartAt] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(true);
  const pageSize = 100;
  const [loading, setLoading] = React.useState(false);
  const [totalCount, setTotalCount] = React.useState<number | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasRrn, setHasRrn] = React.useState(false);
  const [hasParentField, setHasParentField] = React.useState(false);
  const [canAddRow, setCanAddRow] = React.useState(false);
  const [canDeleteRow, setCanDeleteRow] = React.useState(false);
  const [isUpdatingValue, setIsUpdatingValue] = React.useState(false);
  const [selectedRowData, setSelectedRowData] = React.useState<
    Array<SpreadsheetTableDefaultDataItem>
  >([]);
  const columnModelRef = React.useRef<any>(null);
  const formatDetailsRef = React.useRef<any>(null);
  const queryDetailsRef = React.useRef<any>(null);
  const loadingRef = React.useRef(false);
  const requestedStarts = React.useRef<Set<number>>(new Set());
  const activeFilterRef = React.useRef<
    { whereClauses: SpreadsheetTableWhereClause[]; andOR?: number } | undefined
  >(undefined);
  const currentSpreadsheetIdRef = React.useRef<number | null>(null);

  // Get UI column IDs (indices) for selection logic to avoid duplicate field ids
  const columnIds = React.useMemo(
    () => columns.map((_, idx) => idx),
    [columns]
  );
  const serialColumnIndex = React.useMemo(
    () => columns.findIndex((c) => c.id === SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID),
    [columns]
  );

  // Update canAddRow and canDeleteRow when selection or hasRrn changes
  React.useEffect(() => {
    setCanAddRow(hasParentField);
    setCanDeleteRow(hasRrn);
  }, [selectionState.selection, columns, hasRrn, selectedColumnIndex]);

  React.useEffect(() => {
    if (props.clearSelectionEvent) {
      setSelectionState(createSelectionState());
      setRowSelectionState(clearRowSelection(rowSelectionState));
      setSelectedRowData([]);
      setHasRrn(false);
      setHasParentField(false);
      props.onSetClearSelectionEvent(false)
    }
  }, [props.clearSelectionEvent])

  // Keyboard event handler for copy, cut, paste, undo, and redo functionality
  const handleKeyDown = React.useCallback(
    async (event: KeyboardEvent) => {
      // Don't interfere with paste events in input fields (like filter inputs)
      const target = event.target as HTMLElement;
      const isInputField = target && (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.contentEditable === 'true' ||
        target.closest('input') ||
        target.closest('textarea')
      );

      // Check if we have either cell selection or row selection
      const hasCellSelection = !!selectionState.selection;
      const hasRowSelection = rowSelectionState.isRowSelectionMode && rowSelectionState.selectedRows.size > 0;
      const hasAnySelection = hasCellSelection || hasRowSelection;

      if (isCopyKeyCombination(event) && hasAnySelection && !isInputField) {
        event.preventDefault();
        event.stopPropagation();
        await handleCopy();
      } else if (isCutKeyCombination(event) && hasAnySelection && !isInputField) {
        event.preventDefault();
        event.stopPropagation();
        await handleCut();
      } else if (isPasteKeyCombination(event) && hasAnySelection && !isInputField) {
        event.preventDefault();
        event.stopPropagation();
        await handlePaste();
      } else if (isUndoKeyCombination(event)) {
        event.preventDefault();
        event.stopPropagation();
        if (props.onUndo) {
          props.onUndo();
        }
      } else if (isRedoKeyCombination(event)) {
        event.preventDefault();
        event.stopPropagation();
        if (props.onRedo) {
          props.onRedo();
        }
      } else if (isSelectAllKeyCombination(event)) {
        event.preventDefault();
        event.stopPropagation();

        if (data.length > 0 && columns.length > 0) {
          const startCell: SpreadsheetTableCellSelection = {
            rowIndex: 0,
            columnId: 0 as any,
          };
          const endCell: SpreadsheetTableCellSelection = {
            rowIndex: data.length - 1,
            columnId: (columns.length - 1) as any,
          };

          const selectAllSelection = createSelection(startCell, endCell);
          setSelectionState(
            updateSelection(selectionState, selectAllSelection)
          );
        }
      } else if (event.key === 'Enter') {
        // Enter should enable edit mode on the currently selected cell
        const target = event.target as HTMLElement;
        const isInputField = target && (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.contentEditable === 'true' ||
          target.closest('input') ||
          target.closest('textarea')
        );
        if (!isInputField && selectionState.selection) {
          event.preventDefault();
          event.stopPropagation();
          try {
            const { start } = selectionState.selection;
            const rowIndex = start.rowIndex;
            const columnId = start.columnId;
            // Notify the specific cell to enter edit mode (include spreadsheet id)
            window.dispatchEvent(new CustomEvent('spreadsheet:editCell', {
              detail: { rowIndex, columnId, spreadsheetId: props.spreadsheetId }
            }));
          } catch {}
        }
      }
    },
    [data, columns, selectionState, rowSelectionState, internalClipboard, props.onUndo, props.onRedo]
  );

  // Copy handler
  const handleCopy = React.useCallback(async () => {
    if (getActiveSpreadsheetId() !== props.spreadsheetId) {
      return;
    }
    // Handle row selection copy
    if (
      rowSelectionState.isRowSelectionMode &&
      rowSelectionState.selectedRows.size > 0
    ) {
      // Create a selection that spans all selected rows but excludes serial number column
      const selectedRowIndices = Array.from(
        rowSelectionState.selectedRows
      ).sort((a, b) => a - b);
      const firstRow = selectedRowIndices[0];
      const lastRow = selectedRowIndices[selectedRowIndices.length - 1];

      if (firstRow !== undefined && lastRow !== undefined) {
        // Exclude serial number column from row copy
        const nonSerialColumns = columnIds.filter((id) => id !== serialColumnIndex);
        if (nonSerialColumns.length > 0) {
          const firstNonSerialColumn = nonSerialColumns[0];
          const lastNonSerialColumn =
            nonSerialColumns[nonSerialColumns.length - 1];

          if (
            firstNonSerialColumn !== undefined &&
            lastNonSerialColumn !== undefined
          ) {
            const rowSelection = {
              start: { rowIndex: firstRow, columnId: firstNonSerialColumn },
              end: { rowIndex: lastRow, columnId: lastNonSerialColumn },
            };

            const clipboardData = formatSpreadsheetDataForClipboard(
              data,
              columns,
              rowSelection
            );
            const success = await copyToClipboard(clipboardData);
            if (success) {
              setInternalClipboard(clipboardData.text);
            } else {
              console.error("Failed to copy row data");
            }
            return;
          }
        }
      }
    }

    // Handle cell selection copy
    if (!selectionState.selection) return;

    const clipboardData = formatSpreadsheetDataForClipboard(
      data,
      columns,
      selectionState.selection
    );
    const success = await copyToClipboard(clipboardData);

    if (success) {
      // Store both text and structured data for better cross-spreadsheet compatibility
      setInternalClipboard(clipboardData.text);
    } else {
      console.error("Failed to copy data");
    }
  }, [data, columns, selectionState.selection, rowSelectionState, columnIds]);

  // Cut handler with API calls and undo/redo tracking
  const handleCut = React.useCallback(async () => {
    let loaderShown = false;
    const loaderTimer = setTimeout(() => {
      loaderShown = true;
      setIsUpdatingValue(true);
    }, 500);
    // Handle row selection cut
    if (
      rowSelectionState.isRowSelectionMode &&
      rowSelectionState.selectedRows.size > 0
    ) {
      // First copy the row data
      await handleCopy();

      // Then clear all selected rows with API calls
      const selectedRowIndices = Array.from(rowSelectionState.selectedRows);

      // Store original values for undo tracking
      const originalValues: Array<{
        rowIndex: number;
        columnId: SpreadsheetTableColumnId;
        originalValue: string | number;
        payload: CellUpdatePayload;
      }> = [];

      // Collect all cells to be cut and their original values (excluding serial numbers)
      for (const rowIndex of selectedRowIndices) {
        const rowData = data[rowIndex];
        if (rowData) {
          for (let colIndex = 0; colIndex < columnIds.length; colIndex++) {
            const columnId = columnIds[colIndex];

            // Skip serial number column when cutting rows
            if (columnId === serialColumnIndex) {
              continue;
            }

            const selectedColumn = columns[columnId as number];

            if (selectedColumn && selectedColumn.accessorKey) {
              const originalValue = (selectedColumn?.typeId === SPREADSHEET_TABLE_CONSTANTS.FIELD_TYPES.IMAGE ? rowData[selectedColumn.accessorKey].name : rowData[selectedColumn.accessorKey]) || "";

              // Get cell data for API payload
              const cellData = dataResult?.data?.[rowIndex]?.find(
                (cell: any) => cell?.field?.fieldid === selectedColumn.id
              );

              if (cellData) {
                // Skip cutting system fields
                if (cellData.field?.system) {
                  continue;
                }

                const payload: CellUpdatePayload = {
                  globalId:
                    (dataResult?.rows?.[rowIndex] as any)?.globalid || 0,
                  fieldId: selectedColumn.id as number,
                  context:
                    props.spreadsheetType === SpreadsheetType.individual
                      ? SPREADSHEET_TYPE.individual
                      : SPREADSHEET_TYPE.pedigree,
                  systemField: cellData.field?.system || false,
                  ds: false,
                  value: "", // Blank value for cut operation
                  rrn: cellData.rrn || -1,
                  tableId: selectedColumn.parentField?.fieldid || 0,
                };

                originalValues.push({
                  rowIndex,
                  columnId: selectedColumn.id as number, // use field id for undo/redo
                  originalValue,
                  payload,
                });
              }
            }
          }
        }
      }

      // Make API calls for each cell to be cut
      const apiPromises = originalValues.map(
        async ({ payload, rowIndex, columnId, originalValue }) => {
          try {
            await updateCellValue(payload);

            // Track for undo/redo
            if (props.trackCellEdit) {
              const previousPayload = { ...payload, value: originalValue };
              props.trackCellEdit(
                payload,
                rowIndex,
                columnId as number,
                previousPayload
              );
            }

            return { success: true, rowIndex, columnId };
          } catch (error) {
            console.error(
              `Failed to cut cell at row ${rowIndex}, column ${columnId}:`,
              error
            );
            return { success: false, rowIndex, columnId, error };
          }
        }
      );

      // Wait for all API calls to complete
      const results = await Promise.all(apiPromises);
      const failedCuts = results.filter((result) => !result.success);

      if (failedCuts.length > 0) {
        console.error("Some cells failed to cut:", failedCuts);
        if (props.showErrorMessage) {
          props.showErrorMessage("Failed to cut some cells. Please try again.");
        }
        return;
      }

      // Update UI state only after successful API calls
      const newData = [...data];
      for (const { rowIndex, columnId } of originalValues) {
        const selectedColumn = columns.find((c) => c.id === (columnId as number));
        if (selectedColumn && selectedColumn.accessorKey && newData[rowIndex]) {
          newData[rowIndex] = {
            ...newData[rowIndex],
            [selectedColumn.accessorKey]: "",
            id: newData[rowIndex]?.id ?? "0",
          };
        }
      }

      setData(newData);
      setIsUpdatingValue(false)
      clearTimeout(loaderTimer);
      if (loaderShown) {
        setIsUpdatingValue(false);
      }
      return;
    }

    // Handle cell selection cut
    if (!selectionState.selection) return;

    // First copy
    await handleCopy();

    // Then clear all selected cells with API calls
    const { start, end } = selectionState.selection;
    const startRow = Math.min(start.rowIndex, end.rowIndex);
    const endRow = Math.max(start.rowIndex, end.rowIndex);
    const startCol = Math.min(
      columnIds.indexOf(start.columnId),
      columnIds.indexOf(end.columnId)
    );
    const endCol = Math.max(
      columnIds.indexOf(start.columnId),
      columnIds.indexOf(end.columnId)
    );

    // Store original values for undo tracking
    const originalValues: Array<{
      rowIndex: number;
      columnId: SpreadsheetTableColumnId;
      originalValue: string | number;
      payload: CellUpdatePayload;
    }> = [];

    // Collect all cells to be cut and their original values
    for (let rowIndex = startRow; rowIndex <= endRow; rowIndex++) {
      const rowData = data[rowIndex];
      if (rowData) {
        for (let colIndex = startCol; colIndex <= endCol; colIndex++) {
          const columnId = columnIds[colIndex];
          const selectedColumn = columns[columnId as number];

          if (selectedColumn && selectedColumn.accessorKey) {
            const originalValue = (selectedColumn?.typeId === SPREADSHEET_TABLE_CONSTANTS.FIELD_TYPES.IMAGE ? rowData[selectedColumn.accessorKey].name : rowData[selectedColumn.accessorKey]) || "";

            // Get cell data for API payload
            const cellData = dataResult?.data?.[rowIndex]?.[columnId as number];

            if (cellData) {
              // Skip cutting system fields
              if (cellData.field?.system) {
                continue;
              }

              const payload: CellUpdatePayload = {
                globalId: (dataResult?.rows?.[rowIndex] as any)?.globalid || 0,
                fieldId: selectedColumn.id as number,
                context:
                  props.spreadsheetType === SpreadsheetType.individual
                    ? SPREADSHEET_TYPE.individual
                    : SPREADSHEET_TYPE.pedigree,
                systemField: cellData.field?.system || false,
                ds: false,
                value: "", // Blank value for cut operation
                rrn: cellData.rrn || -1,
                tableId: selectedColumn.parentField?.fieldid || 0,
              };

              originalValues.push({
                rowIndex,
                columnId: columnId as number, // use field id for undo/redo
                originalValue,
                payload,
              });
            }
          }
        }
      }
    }

    // Make API calls for each cell to be cut
    const apiPromises = originalValues.map(
      async ({ payload, rowIndex, columnId, originalValue }) => {
        try {
          await updateCellValue(payload);

          // Track for undo/redo
          if (props.trackCellEdit) {
            const previousPayload = { ...payload, value: originalValue };
            props.trackCellEdit(
              payload,
              rowIndex,
              columnId as number,
              previousPayload
            );
          }

          return { success: true, rowIndex, columnId };
        } catch (error) {
          console.error(
            `Failed to cut cell at row ${rowIndex}, column ${columnId}:`,
            error
          );
          return { success: false, rowIndex, columnId, error };
        }
      }
    );

    // Wait for all API calls to complete
    const results = await Promise.all(apiPromises);
    const failedCuts = results.filter((result) => !result.success);

    if (failedCuts.length > 0) {
      console.error("Some cells failed to cut:", failedCuts);
      if (props.showErrorMessage) {
        props.showErrorMessage("Failed to cut some cells. Please try again.");
      }
      return;
    }

    // Update UI state only after successful API calls
    const newData = [...data];
    for (const { rowIndex, columnId } of originalValues) {
      const selectedColumn = columns[columnId as number];
      if (selectedColumn && selectedColumn.accessorKey && newData[rowIndex]) {
        newData[rowIndex] = {
          ...newData[rowIndex],
          [selectedColumn.accessorKey]: "",
          id: newData[rowIndex]?.id ?? "0",
        };
      }
    }

    clearTimeout(loaderTimer);
    if (loaderShown) {
      setIsUpdatingValue(false);
    }
    setData(newData);
  }, [
    data,
    columns,
    selectionState.selection,
    rowSelectionState,
    handleCopy,
    columnIds,
    dataResult,
    props,
  ]);

  // Paste handler - Enhanced with API calls and undo/redo tracking
  const handlePaste = React.useCallback(async () => {
    // Determine the target selection (row selection or cell selection)
    if (getActiveSpreadsheetId() !== props.spreadsheetId) {
      return;
    }
    let targetSelection = selectionState.selection;
    let isRowPaste = false;
    let loaderShown = false;
    const loaderTimer = setTimeout(() => {
      loaderShown = true;
      setIsUpdatingValue(true);
    }, 500);
    // If we have row selection, create a selection that spans all selected rows but excludes serial number column
    if (
      rowSelectionState.isRowSelectionMode &&
      rowSelectionState.selectedRows.size > 0
    ) {
      const selectedRowIndices = Array.from(
        rowSelectionState.selectedRows
      ).sort((a, b) => a - b);
      const firstRow = selectedRowIndices[0];
      const lastRow = selectedRowIndices[selectedRowIndices.length - 1];

      if (firstRow !== undefined && lastRow !== undefined) {
        // For row paste, exclude serial number column and start from second column
        const nonSerialColumns = columnIds.filter((id) => id !== serialColumnIndex);
        if (nonSerialColumns.length > 0) {
          const firstNonSerialColumn = nonSerialColumns[0];
          const lastNonSerialColumn =
            nonSerialColumns[nonSerialColumns.length - 1];

          if (
            firstNonSerialColumn !== undefined &&
            lastNonSerialColumn !== undefined
          ) {
            targetSelection = {
              start: { rowIndex: firstRow, columnId: firstNonSerialColumn },
              end: { rowIndex: lastRow, columnId: lastNonSerialColumn },
            };
            isRowPaste = true; // Flag to indicate this is a row paste operation
          }
        }
      }
    }

    if (!targetSelection) return;

    try {
      const clipboardText = await readFromClipboard();
      let pasteData: string[];

      if (clipboardText) {
        // Check if clipboard data is tabular (multiple rows/columns)
        if (isTabularClipboardData(clipboardText)) {
          const matrix = parseClipboardDataAsMatrix(clipboardText);
          // Flatten the matrix for pasting
          pasteData = matrix.flat();
        } else {
          pasteData = parseClipboardDataForPaste(clipboardText);
        }
      } else if (internalClipboard) {
        pasteData = [internalClipboard];
      } else {
        return;
      }

      if (pasteData.length > 0) {
        const { start, end } = targetSelection;
        const startRow = Math.min(start.rowIndex, end.rowIndex);
        const endRow = Math.max(start.rowIndex, end.rowIndex);
        const startCol = Math.min(
          columnIds.indexOf(start.columnId),
          columnIds.indexOf(end.columnId)
        );
        const endCol = Math.max(
          columnIds.indexOf(start.columnId),
          columnIds.indexOf(end.columnId)
        );

        // Store original values and prepare paste operations
        const pasteOperations: Array<{
          rowIndex: number;
          columnId: SpreadsheetTableColumnId;
          newValue: string;
          originalValue: string | number;
          payload: CellUpdatePayload;
          type: number;
        }> = [];

        let pasteIndex = 0;

        // Collect all cells to be pasted and their original values
        for (
          let rowIndex = startRow;
          rowIndex <= endRow && pasteIndex < pasteData.length;
          rowIndex++
        ) {
          const rowData = data[rowIndex];
          if (rowData) {
            for (
              let colIndex = startCol;
              colIndex <= endCol && pasteIndex < pasteData.length;
              colIndex++
            ) {
              const columnId = columnIds[colIndex];
              const selectedColumn = columns[columnId as number];

              if (selectedColumn && selectedColumn.accessorKey) {
                const newValue = pasteData[pasteIndex];
                const originalValue = (selectedColumn?.typeId === SPREADSHEET_TABLE_CONSTANTS.FIELD_TYPES.IMAGE ? rowData[selectedColumn.accessorKey].name : rowData[selectedColumn.accessorKey]) || "";
                
                // Skip pasting into readonly/system fields
                if (!selectedColumn.editable) {
                  pasteIndex++;
                  continue;
                }
                
                // Get cell data for API payload
                const cellData = dataResult?.data?.[rowIndex]?.find(
                  (cell: any) => cell?.field?.fieldid === selectedColumn.id
                );

                if (cellData) {
                  const payload: CellUpdatePayload = {
                    globalId:
                      (dataResult?.rows?.[rowIndex] as any)?.globalid || 0,
                    fieldId: selectedColumn.id as number,
                    context:
                      props.spreadsheetType === SpreadsheetType.individual
                        ? SPREADSHEET_TYPE.individual
                        : SPREADSHEET_TYPE.pedigree,
                    systemField: cellData.field?.system || false,
                    ds: false,
                    value: newValue || "", // New value for paste operation
                    rrn: cellData.rrn || -1,
                    tableId: selectedColumn.parentField?.fieldid || 0,
                  };
                  

                  pasteOperations.push({
                    rowIndex,
                    columnId: columnId || 0, // use field id for undo/redo
                    newValue: newValue || "",
                    originalValue,
                    payload,
                    type:Number(cellData.field?.fieldtype?.typeid),
                  });
                }
                pasteIndex++;
              } else if (selectedColumn && !selectedColumn.editable) {
                // Skip pasting into readonly/system fields but still increment paste index
                pasteIndex++;
              }
            }
          }
        }

        // Check if this spreadsheet is currently active before making API calls
        const spreadsheetType = props.spreadsheetType === SpreadsheetType.individual ? 'individual' : 'pedigree';
        const spreadsheetId = String(props.spreadsheetId);
        
        // Validate date fields and filter out invalid dates
        const validPasteOperations: typeof pasteOperations = [];
        let hasInvalidDates = false;
        
        pasteOperations.forEach(operation => {
          // Check if this is a date field (typeId === 2)
          if (operation.type === 2) {
            const trimmedValue = (operation.newValue || "").trim();
            if (trimmedValue !== "") {
              const validation = validateSpreadsheetDateInput(trimmedValue);
              if (!validation.isValid) {
                // Mark that we have invalid dates (show message once after loop)
                hasInvalidDates = true;
                if (props.showInfoMessage) {
                  props.showInfoMessage(getSpreadsheetInvalidDateMessage());
                }
                // Skip this operation - don't add to validPasteOperations
                return;
              }
              // If valid, use normalized value for API call
              if (validation.normalizedValue !== undefined) {
                operation.newValue = validation.normalizedValue;
                operation.payload.value = validation.normalizedValue;
              }
            }
          }
          // Add valid operation to the list
          validPasteOperations.push(operation);
        });
        
        // Show error message once if there were invalid dates
        if (hasInvalidDates) {
          // If all dates were invalid, stop here
          if (validPasteOperations.length === 0) {
            return;
          }
        }
        
        // Make API calls for each valid cell to be pasted
        const apiPromises = validPasteOperations.map(
          async ({ payload, rowIndex, columnId, newValue, originalValue }) => {
            try {
              await updateCellValue(payload);

              // Track for undo/redo
              if (props.trackCellEdit) {
                const previousPayload = { ...payload, value: originalValue };
                props.trackCellEdit(
                  payload,
                  rowIndex,
                  columnId,
                  previousPayload
                );
              }

              return { success: true, rowIndex, columnId };
            } catch (error) {
              console.error(
                `Failed to paste cell at row ${rowIndex}, column ${columnId}:`,
                error
              );
              return { success: false, rowIndex, columnId, error };
            }
          }
        );

        // Wait for all API calls to complete
        const results = await Promise.all(apiPromises);
        const failedPastes = results.filter((result) => !result.success);

        if (failedPastes.length > 0) {
          console.error("Some cells failed to paste:", failedPastes);
          if (props.showErrorMessage) {
            props.showErrorMessage(
              "Failed to paste some cells. Please try again."
            );
          }
          return;
        }

        // Update UI state only after successful API calls
        const newData = [...data];
        for (const { rowIndex, columnId, newValue } of validPasteOperations) {
          const selectedColumn = columns[columnId as number];
          const updatedValue = selectedColumn?.typeId === SPREADSHEET_TABLE_CONSTANTS.FIELD_TYPES.IMAGE ? { name: newValue } : newValue;
          if (
            selectedColumn &&
            selectedColumn.accessorKey &&
            newData[rowIndex]
          ) {
            newData[rowIndex] = {
              ...newData[rowIndex],
              [selectedColumn.accessorKey]: updatedValue,
              id: newData[rowIndex]?.id ?? "0",
            };
          }
        }

        setData(newData);
      }
    } catch (error) {
      console.error("Failed to paste data:", error);
      if (props.showErrorMessage) {
        props.showErrorMessage("Failed to paste data. Please try again.");
      }
    }
    finally {
      clearTimeout(loaderTimer);
      if (loaderShown) {
        setIsUpdatingValue(false);
      }
    }
  }, [
    data,
    columns,
    selectionState.selection,
    rowSelectionState,
    internalClipboard,
    columnIds,
    dataResult,
    props,
  ]);

  // Fill handlers with API calls and undo/redo tracking
  const handleFillDown = React.useCallback(async () => {
    if (!selectionState.selection) {
      return;
    }

    const { start, end } = selectionState.selection;
    const startRow = Math.min(start.rowIndex, end.rowIndex);
    const endRow = Math.max(start.rowIndex, end.rowIndex);
    
    // Get all selected columns
    const startColIndex = columnIds.indexOf(start.columnId);
    const endColIndex = columnIds.indexOf(end.columnId);
    const minColIndex = Math.min(startColIndex, endColIndex);
    const maxColIndex = Math.max(startColIndex, endColIndex);
    const selectedColumnIds = columnIds.slice(minColIndex, maxColIndex + 1);

    const sourceRowData = data[startRow];

    if (sourceRowData) {
      let loaderShown = false;
      const loaderTimer = setTimeout(() => {
        loaderShown = true;
        setIsUpdatingValue(true);
      }, 500);
      // Store original values and prepare fill operations
      const fillOperations: Array<{
        rowIndex: number;
        columnId: SpreadsheetTableColumnId;
        newValue: string | number;
        originalValue: string | number;
        payload: CellUpdatePayload;
        columnIndex: number
      }> = [];

      // Process each selected column
      for (const columnId of selectedColumnIds) {
        const selectedColumn = columns[columnId as number];
        
        if (selectedColumn && selectedColumn.accessorKey && selectedColumn.editable) {
          let sourceValue = sourceRowData[selectedColumn.accessorKey];
          if (sourceValue && typeof sourceValue === 'object') {
            sourceValue = sourceValue.name;
          }

          // Collect all cells to be filled and their original values for this column
          for (let rowIndex = startRow + 1; rowIndex <= endRow; rowIndex++) {
            const rowData = data[rowIndex];
            if (rowData) {
              let originalValue = rowData[selectedColumn.accessorKey] || "";
              if (originalValue && typeof originalValue === 'object') {
                originalValue = originalValue.name;
              }
              
              // Get cell data for API payload
              const cellData = dataResult?.data?.[rowIndex]?.find(
                (cell: any) => cell?.field?.fieldid === selectedColumn.id
              );

              if (cellData) {
                const payload: CellUpdatePayload = {
                  globalId: dataResult?.rows?.[rowIndex]?.globalid || 0,
                  fieldId: selectedColumn.id,
                  context: props.spreadsheetType === SpreadsheetType.individual
                    ? SPREADSHEET_TYPE.individual 
                    : SPREADSHEET_TYPE.pedigree,
                  systemField: cellData.field?.system || false,
                  ds: false,
                  value: sourceValue, // Source value for fill operation
                  rrn: cellData.rrn || -1,
                  tableId: selectedColumn.parentField?.fieldid || 0,
                };

                fillOperations.push({
                  rowIndex,
                  columnId: selectedColumn.id as number, // use field id for undo/redo
                  newValue: sourceValue,
                  originalValue,
                  payload,
                  columnIndex: columnId
                });
              }
            }
          }
        }
      }

      // Track the entire fill operation as a single action for undo/redo
      if (fillOperations.length > 0 && props.trackBulkFill) {
        props.trackBulkFill(fillOperations, 'down');
      }

      // Use bulk update for all fill operations at once
      if (fillOperations.length > 0 && props.onBulkCellUpdate) {
        try {
          const bulkUpdates = fillOperations.map(operation => ({
            rowIndex: operation.rowIndex,
            columnId: operation.columnId,
            value: operation.newValue,
            payload: operation.payload,
            columnIndex: operation.columnIndex
          }));

          await props.onBulkCellUpdate(bulkUpdates);
        } catch (error) {
          console.error("Failed to fill down cells:", error);
          if (props.showErrorMessage) {
            props.showErrorMessage(
              "Failed to fill down some cells. Please try again."
            );
          }
          return;
        }
      }
      clearTimeout(loaderTimer);
      if (loaderShown) {
        setIsUpdatingValue(false);
      }
    }
  }, [data, columns, selectionState.selection, dataResult, props, columnIds]);

  const handleFillUp = React.useCallback(async () => {
    if (!selectionState.selection) {
      return;
    }

    const { start, end } = selectionState.selection;
    const startRow = Math.min(start.rowIndex, end.rowIndex);
    const endRow = Math.max(start.rowIndex, end.rowIndex);
    
    // Get all selected columns
    const startColIndex = columnIds.indexOf(start.columnId);
    const endColIndex = columnIds.indexOf(end.columnId);
    const minColIndex = Math.min(startColIndex, endColIndex);
    const maxColIndex = Math.max(startColIndex, endColIndex);
    const selectedColumnIds = columnIds.slice(minColIndex, maxColIndex + 1);

    const sourceRowData = data[endRow];

    if (sourceRowData) {
      let loaderShown = false;
      const loaderTimer = setTimeout(() => {
        loaderShown = true;
        setIsUpdatingValue(true);
      }, 500);
      // Store original values and prepare fill operations
      const fillOperations: Array<{
        rowIndex: number;
        columnId: SpreadsheetTableColumnId;
        newValue: string | number;
        originalValue: string | number;
        payload: CellUpdatePayload;
        columnIndex: number
      }> = [];

      // Process each selected column
      for (const columnId of selectedColumnIds) {
        const selectedColumn = columns[columnId as number];
        
        if (selectedColumn && selectedColumn.accessorKey && selectedColumn.editable) {
          let sourceValue = sourceRowData[selectedColumn.accessorKey];
          if (sourceValue && typeof sourceValue === 'object') {
            sourceValue = sourceValue.name;
          }

          // Collect all cells to be filled and their original values for this column
          for (let rowIndex = startRow; rowIndex < endRow; rowIndex++) {
            const rowData = data[rowIndex];
            if (rowData) {
              let originalValue = rowData[selectedColumn.accessorKey] || "";
              if (originalValue && typeof originalValue === 'object') {
                originalValue = originalValue.name;
              }
              
              // Get cell data for API payload
              const cellData = dataResult?.data?.[rowIndex]?.find(
                (cell: any) => cell?.field?.fieldid === selectedColumn.id
              );

              if (cellData) {
                const payload: CellUpdatePayload = {
                  globalId: dataResult?.rows?.[rowIndex]?.globalid || 0,
                  fieldId: selectedColumn.id,
                  context: props.spreadsheetType === SpreadsheetType.individual
                    ? SPREADSHEET_TYPE.individual 
                    : SPREADSHEET_TYPE.pedigree,
                  systemField: cellData.field?.system || false,
                  ds: false,
                  value: sourceValue, // Source value for fill operation
                  rrn: cellData.rrn || -1,
                  tableId: selectedColumn.parentField?.fieldid || 0,
                };

                fillOperations.push({
                  rowIndex,
                  columnId: selectedColumn.id as number, // use field id for undo/redo
                  newValue: sourceValue,
                  originalValue,
                  payload,
                  columnIndex: columnId
                });
              }
            }
          }
        }
      }

      // Track the entire fill operation as a single action for undo/redo
      if (fillOperations.length > 0 && props.trackBulkFill) {
        props.trackBulkFill(fillOperations, 'up');
      }

      // Use bulk update for all fill operations at once
      if (fillOperations.length > 0 && props.onBulkCellUpdate) {
        try {
          const bulkUpdates = fillOperations.map(operation => ({
            rowIndex: operation.rowIndex,
            columnId: operation.columnId,
            value: operation.newValue,
            payload: operation.payload,
            columnIndex: operation.columnIndex
          }));

          await props.onBulkCellUpdate(bulkUpdates);
        } catch (error) {
          console.error("Failed to fill up cells:", error);
          if (props.showErrorMessage) {
            props.showErrorMessage(
              "Failed to fill up some cells. Please try again."
            );
          }
          return;
        }
      }
      clearTimeout(loaderTimer);
      if (loaderShown) {
        setIsUpdatingValue(false);
      }
    }
  }, [data, columns, selectionState.selection, dataResult, props, columnIds]);

  // Context menu handlers
  const handleContextMenu = React.useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({ x: event.clientX, y: event.clientY });
  }, []);

  const closeContextMenu = React.useCallback(() => {
    setContextMenu(null);
    setContextMenuCell(null);
  }, []);

  const getContextMenuRowData = React.useCallback(() => {
    const rowIndex =
      contextMenuCell?.rowIndex ?? selectionState.selection?.start.rowIndex;

    if (rowIndex === undefined || rowIndex === null) {
      return undefined;
    }

    return data[rowIndex];
  }, [contextMenuCell, selectionState.selection, data]);

  // Open Datasheet handler
  const handleOpenDatasheet = React.useCallback(() => {
    const rowData = getContextMenuRowData();

    if (rowData && rowData.globalId) {
      if (props.spreadsheetType === SpreadsheetType.pedigree) {
        openPedigreeDatasheet(String(rowData.globalId));
      } else {
        openDatasheetFromIndividual(rowData.globalId);
      }
    }
  }, [getContextMenuRowData, props.spreadsheetType]);

  // Open Pedigree handler
  const handleOpenPedigree = React.useCallback(() => {
    const rowData = getContextMenuRowData();
    
    if (rowData && rowData.globalId) {
      if (props.spreadsheetType === SpreadsheetType.pedigree) {
        openPedigreeFromPedigrees(String(rowData.globalId));
      } else {
        openPedigreeFromIndividual(rowData.globalId);
      }
    }
  }, [getContextMenuRowData, props.spreadsheetType]);

  // Professional Drag-and-Select Implementation
  const [dragState, setDragState] = React.useState<{
    isDragging: boolean;
    startCell: { rowIndex: number; columnId: SpreadsheetTableColumnId } | null;
    currentCell: {
      rowIndex: number;
      columnId: SpreadsheetTableColumnId;
    } | null;
  }>({
    isDragging: false,
    startCell: null,
    currentCell: null,
  });

  // Mouse down handler - Start selection
  const handleCellMouseDown = React.useCallback(
    (
      rowIndex: number,
      columnId: SpreadsheetTableColumnId,
      dataItem: SpreadsheetTableDefaultDataItem,
      columnName: SpreadsheetTableColumnName,
      event?: React.MouseEvent
    ) => {
      // Prevent text selection and default behaviors
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      // Store currently editing cell using actual field id for compatibility with updates
      //const actualFieldId = columns[columnId as number]?.id as number | undefined;
      setCurrentlyEditingCell({ rowIndex, columnId })
      // Check if this is a right-click (context menu)
      const isRightClick = event?.button === 2;
      if (isRightClick) {
        setContextMenuCell({ rowIndex, columnId });
      } else if (contextMenuCell) {
        setContextMenuCell(null);
      }

      // Close context menu on left-click
      if (!isRightClick && contextMenu) {
        setContextMenu(null);
      }
      const hasAnyParentField = smartColumns.some((col) => col.parentField);
      setHasParentField(hasAnyParentField);

      // Handle right-click behavior
      if (isRightClick) {
        // Check if we're in row selection mode
        if (
          rowSelectionState.isRowSelectionMode &&
          rowSelectionState.selectedRows.has(rowIndex)
        ) {
          // If right-clicking on a row that's part of the current row selection,
          // keep the row selection and just update the selected row data
          const selectedRowsData: SpreadsheetTableDefaultDataItem[] = [];
          for (const selectedRowIndex of rowSelectionState.selectedRows) {
            const rowData = data[selectedRowIndex];
            if (rowData) {
              selectedRowsData.push(rowData);
            }
          }
          setSelectedRowData(selectedRowsData);
          const hasAnyRrn = selectedRowsData.some((row) => row?.rrn);
          setHasRrn(hasAnyRrn);
          setSelectedColumnIndex(columnId);
          return; // Don't change selection for right-click when clicking within current row selection
        }

        // Handle cell selection right-click
        if (selectionState.selection) {
          // Check if the right-clicked cell is part of the current selection
          const isCellInCurrentSelection = isCellInSelection(
            rowIndex,
            columnId,
            selectionState.selection,
            columnIds
          );

          if (isCellInCurrentSelection) {
            // If right-clicking on a cell that's part of the current selection,
            // keep the selection and just update the selected row data
            const { start, end } = selectionState.selection;
            const startRow = Math.min(start.rowIndex, end.rowIndex);
            const endRow = Math.max(start.rowIndex, end.rowIndex);

            // Get all selected row data from the data array
            const selectedRows: SpreadsheetTableDefaultDataItem[] = [];
            for (let i = startRow; i <= endRow; i++) {
              const rowData = data[i];
              if (rowData) {
                selectedRows.push(rowData);
              }
            }
            setSelectedRowData(selectedRows);

            const hasAnyRrn = selectedRows.some((row) => row?.rrn);
            setHasRrn(hasAnyRrn);
            return; // Don't change selection for right-click when clicking within current selection
          }
          // If right-clicking on a cell that's NOT part of the current selection,
          // clear the current selection and select only the right-clicked cell
          // This will fall through to the normal selection logic below
        }
        // If there's no selection, select the right-clicked cell first
        // This will fall through to the normal selection logic below
      }

      // Check if the clicked column is the serial number column (row count column)
      const isSerialNumberColumn = columnId === serialColumnIndex;
      const isShiftClick = event?.shiftKey === true;
      const isCtrlClick = event?.ctrlKey === true;
      const isMetaClick = event?.metaKey === true; // Command key on Mac
      const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
      
      // Set multi-select behavior based on OS
      const isMultiSelectClick = isMac ? isMetaClick : isCtrlClick;

      if (isSerialNumberColumn) {
        // Don't enable dragging from serial number column
        setDragState({
          isDragging: false,
          startCell: null,
          currentCell: null,
        });

        // Clear context menu when clicking on serial number column (unless it's a right-click)
        if (!isRightClick && contextMenu) {
          setContextMenu(null);
        }

        // Clear cell selection when doing row selection
        setSelectionState(createSelectionState());

        if (isMultiSelectClick && !isShiftClick) {
          // Cmd+click (Mac) or Ctrl+click (Windows): Toggle row selection (add/remove from selection)
          const newRowSelectionState = toggleRowSelection(
            rowSelectionState,
            rowIndex
          );
          setRowSelectionState(newRowSelectionState);

          // Update selectedRowData with all selected rows
          const selectedRowsData: SpreadsheetTableDefaultDataItem[] = [];
          for (const selectedRowIndex of newRowSelectionState.selectedRows) {
            const rowData = data[selectedRowIndex];
            if (rowData) {
              selectedRowsData.push(rowData);
            }
          }
          setSelectedRowData(selectedRowsData);
          const hasAnyRrn = selectedRowsData.some((row) => row?.rrn);
          setHasRrn(hasAnyRrn);
          setSelectedColumnIndex(columnId);
        } else if (isShiftClick && !isMultiSelectClick) {
          // Shift+click: Select range from last selected row to current row
          const newRowSelectionState = selectRowRange(
            rowSelectionState,
            rowIndex
          );
          setRowSelectionState(newRowSelectionState);

          // Update selectedRowData with all rows in range
          const selectedRowsData: SpreadsheetTableDefaultDataItem[] = [];
          for (const selectedRowIndex of newRowSelectionState.selectedRows) {
            const rowData = data[selectedRowIndex];
            if (rowData) {
              selectedRowsData.push(rowData);
            }
          }
          setSelectedRowData(selectedRowsData);
          const hasAnyRrn = selectedRowsData.some((row) => row?.rrn);
          setHasRrn(hasAnyRrn);
          setSelectedColumnIndex(columnId);
        } else {
          // Regular click: Select single row (clear other selections)
          const newRowSelectionState = selectSingleRow(
            rowSelectionState,
            rowIndex
          );
          setRowSelectionState(newRowSelectionState);

          setSelectedRowData([dataItem]);
          dataItem?.rrn ? setHasRrn(true) : setHasRrn(false);
          setSelectedColumnIndex(columnId);
        }

        // Handle right-click context menu for serial number column
        if (isRightClick) {
          // The row has been selected above, now trigger the context menu
          // Make sure we preserve the row selection state
          if (event) {
            setContextMenu({ x: event.clientX, y: event.clientY });
          }
        }
        
        // Prevent context menu for multi-select clicks (Cmd+click on Mac, Ctrl+click on Windows)
        if (isMultiSelectClick && event) {
          event.preventDefault();
          event.stopPropagation();
        }

        return; // Exit early for row selection
      }

      // Regular cell selection logic for non-serial-number columns
      // Check if this is a right-click on a row-selected cell
      if (
        isRightClick &&
        rowSelectionState.isRowSelectionMode &&
        rowSelectionState.selectedRows.has(rowIndex)
      ) {
        // If right-clicking on a cell that's part of a row selection,
        // preserve the row selection and just show context menu
        if (event) {
          setContextMenu({ x: event.clientX, y: event.clientY });
        }
        return; // Don't change to cell selection mode
      }

      // Clear row selection when clicking on non-serial-number columns (unless it's a right-click on a row-selected cell)
      setRowSelectionState(clearRowSelection(rowSelectionState));

      // Start drag state
      const startCell = { rowIndex, columnId };
      setDragState({
        isDragging: true,
        startCell,
        currentCell: startCell,
      });

      // Create initial selection
      const cellSelection: SpreadsheetTableCellSelection = {
        rowIndex,
        columnId,
      };

      // Initialize selectedRowData with the first selected row
      setSelectedRowData([dataItem]);
      dataItem?.rrn ? setHasRrn(true) : setHasRrn(false);

      // Store the selected column name
      setSelectedColumnIndex(columnId);

      // Create new selection (either for new selection or to replace existing selection)
      const newSelection = createSelection(cellSelection, cellSelection);
      setSelectionState({
        selection: newSelection,
        isSelecting: true,
      });

      // Handle right-click context menu after cell selection
      if (isRightClick) {
        // The cell has been selected above, now trigger the context menu
        if (event) {
          setContextMenu({ x: event.clientX, y: event.clientY });
        }
      }
    },
    [
      selectionState.selection,
      data,
      columnIds,
      rowSelectionState,
      setContextMenu,
      contextMenuCell,
    ]
  );

  // Mouse enter handler - Update selection during drag
  const handleCellMouseEnter = React.useCallback(
    (rowIndex: number, columnId: SpreadsheetTableColumnId) => {
      if (dragState.isDragging && dragState.startCell) {
        const currentCell = { rowIndex, columnId };
        setDragState((prev) => ({ ...prev, currentCell }));

        // Update selection
        const newSelection = createSelection(dragState.startCell, currentCell);
        setSelectionState({
          selection: newSelection,
          isSelecting: true,
        });
      }
    },
    [dragState.isDragging, dragState.startCell]
  );

  // Mouse up handler - End selection
  const handleCellMouseUp = React.useCallback(() => {
    // Get all selected rows based on current selection
    if (selectionState.selection) {
      const { start, end } = selectionState.selection;
      const startRow = Math.min(start.rowIndex, end.rowIndex);
      const endRow = Math.max(start.rowIndex, end.rowIndex);

      // Get all selected row data from the data array
      const selectedRows: SpreadsheetTableDefaultDataItem[] = [];
      for (let i = startRow; i <= endRow; i++) {
        const rowData = data[i];
        if (rowData) {
          selectedRows.push(rowData);
        }
      }
      setSelectedRowData(selectedRows);

      const hasAnyParentField = smartColumns.some((col) => col.parentField);
      setHasParentField(hasAnyParentField);

      const hasAnyRrn = selectedRows.some((row) => row?.rrn);
      setHasRrn(hasAnyRrn);
    }

    setDragState({
      isDragging: false,
      startCell: null,
      currentCell: null,
    });

    setSelectionState((prev) => ({
      ...prev,
      isSelecting: false,
    }));
  }, [selectionState.selection, data]);

  // Global mouse up handler - Safety net
  const handleGlobalMouseUp = React.useCallback(() => {
    if (dragState.isDragging) {
      setDragState({
        isDragging: false,
        startCell: null,
        currentCell: null,
      });
      setSelectionState((prev) => ({
        ...prev,
        isSelecting: false,
      }));
    }
  }, [dragState.isDragging]);

  // Prevent text selection during drag
  const handleGlobalMouseMove = React.useCallback(
    (event: MouseEvent) => {
      if (dragState.isDragging) {
        // Prevent text selection during drag
        event.preventDefault();
      }
    },
    [dragState.isDragging]
  );

  // =============================================================================
  // SORTING FUNCTIONS
  // =============================================================================

  /**
   * Get sorting information for a column from the API data
   * Uses sortOrder and sortAsc from the ISS load format API
   */
  const getColumnSortInfo = (column: any) => {
    if (!column || column.sortOrder === undefined || column.sortOrder <= 0) {
      return null;
    }

    return {
      sortOrder: column.sortOrder,
      sortAsc: column.sortAsc || false,
    };
  };

  /**
   * Apply sorting information to columns from API data
   * Maps the sortOrder and sortAsc from the API to column properties
   */
  const applySortingFromApi = (
    columns: SpreadsheetTableColumn[],
    apiColumns: any[]
  ) => {
    return columns.map((col) => {
      // Find corresponding API column
      const apiColumn = apiColumns.find((apiCol: any) => {
        // Match by field ID or field name
        return (
          apiCol.field?.fieldid === col.id ||
          apiCol.field?.fieldname === col.colName ||
          apiCol.field?.fieldname === col.accessorKey
        );
      });

      if (apiColumn) {
        const sortInfo = getColumnSortInfo(apiColumn);
        return {
          ...col,
          sortOrder: sortInfo?.sortOrder,
          sortAsc: sortInfo?.sortAsc,
        };
      }

      return col;
    });
  };

  // =============================================================================
  // EFFECTS
  // =============================================================================
  React.useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mouseup", handleGlobalMouseUp);
    document.addEventListener("mousemove", handleGlobalMouseMove);
    document.addEventListener("selectstart", (e) => {
      if (dragState.isDragging) {
        e.preventDefault();
      }
    });

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
      document.removeEventListener("mousemove", handleGlobalMouseMove);
    };
  }, [
    handleKeyDown,
    handleGlobalMouseUp,
    handleGlobalMouseMove,
    dragState.isDragging,
  ]);

  // Check if a cell is selected
  const isCellSelected = React.useCallback(
    (rowIndex: number, columnId: SpreadsheetTableColumnId) => {
      const selected = selectionState.selection
        ? isCellInSelection(
            rowIndex,
            columnId,
            selectionState.selection,
            columnIds
          )
        : false;
      return selected;
    },
    [selectionState.selection, columnIds]
  );

  // Create debounced API call for cell updates
  const debouncedCellUpdate = React.useMemo(
    () =>
      debounce(
        async (
          payload: CellUpdatePayload,
          rowIndex: number,
          columnId: SpreadsheetTableColumnId,
          value: string
        ) => {
          try {
            await updateCellValue(payload);

            setData((prevData) => {
              const newData = [...prevData];
              const rowData = newData[rowIndex];
              if (rowData) {
                const selectedColumn = columns[columnId as number];
                if (selectedColumn && selectedColumn.accessorKey && rowData) {
                  newData[rowIndex] = {
                    ...rowData,
                    [selectedColumn.accessorKey]:
                      selectedColumn.typeId == 6 ? { name: value } : value,
                    id: rowData.id,
                  };
                }
              }
              return newData;
            });
          } catch (error) {
            const errorMessage =
              (error as HttpErrorDto)?.entity ||
              I18n.t("Failed to update cell value");
            if (props.showErrorMessage) {
              props.showErrorMessage(errorMessage);
            }
          }
        },
        500
      ),
    [columns, props.showErrorMessage]
  );

  const handleCellValueChange = React.useCallback(
    (
      rowIndex: number,
      columnId: SpreadsheetTableColumnId,
      value: string | number,
      popPreviousUndo: boolean = false
    ) => {
      const currentData = dataResult?.data;
      const currentRowData = dataResult?.rows;
      if (!currentData || !currentRowData) {
        return;
      }

      const selectedCurrentData = currentData[rowIndex]?.[columnId as number]
      const selectedRowData = currentRowData[rowIndex];
      const columnData = columns[columnId as number];

      if (selectedCurrentData && selectedRowData) {
        // Get the previous value from the current UI state (data) instead of backend data
        const currentRowData = data[rowIndex];
        const previousValue =
          currentRowData && columnData?.accessorKey
            ? currentRowData[columnData.accessorKey]
            : selectedCurrentData.rawValue ||
              selectedCurrentData.dataValue ||
              null;

        // Create the current payload (new state)
        const currentPayload: CellUpdatePayload = {
          globalId: props.spreadsheetType === SpreadsheetType.individual ? selectedRowData.globalid : selectedRowData.id,
          fieldId: selectedCurrentData.field?.fieldid || 0,
          context:
            props.spreadsheetType === SpreadsheetType.individual
              ? SPREADSHEET_TYPE.individual
              : SPREADSHEET_TYPE.pedigree,
          systemField: selectedCurrentData.field?.system || false,
          ds: false, // Always false as this is a spreadsheet edit
          value: value,
          rrn: selectedCurrentData.rrn || -1,
          tableId: columnData?.parentField?.fieldid || 0,
        };

        let previouslySetValue = previousValue

        if (previouslySetValue && typeof previouslySetValue === 'object' && 'name' in previouslySetValue) {
          previouslySetValue = previouslySetValue.name;
        }

        // Create the previous payload (state to revert to)
        const previousPayload: CellUpdatePayload = {
          ...currentPayload,
          value: previouslySetValue || "",
        };

        // Track the cell edit for undo/redo with both payloads using parent function
        if (parentTrackCellEdit) {
          parentTrackCellEdit(
            currentPayload,
            rowIndex,
            columnId,
            previousPayload,
            popPreviousUndo
          );
        }

        // Use the debounced update for the actual API call
        debouncedCellUpdate(currentPayload, rowIndex, columnId, value);
      }
    },
    [
      data,
      columns,
      props.spreadsheetType,
      debouncedCellUpdate,
      parentTrackCellEdit,
      dataResult,
    ]
  );

  React.useEffect(() => {
    if (currentlyEditingCell && cellToUpdate) {
      // If we're switching to a different cell, process the pending update
      if (
        (cellToUpdate.rowIndex !== currentlyEditingCell.rowIndex ||
          cellToUpdate.columnId !== currentlyEditingCell.columnId) &&
        cellToUpdate.newValue
      ) {
        handleCellValueChange(
          cellToUpdate.rowIndex,
          cellToUpdate.columnId,
          cellToUpdate.newValue
        );
        setCellToUpdate(null);
      }
    }
  }, [currentlyEditingCell, cellToUpdate]);

  // This function handles table-level clicks to create onBlur like event to value updates
const handleTableClick = React.useCallback((e: React.MouseEvent) => {
  const target = e.target as HTMLElement;
  
  // Check if click is on a cell element
  const cellElement = target.closest('[data-row-index][data-column-id]');
  
  if (!cellElement) {
    // Clicked outside any cell - process any pending updates
    if (cellToUpdate) {
      handleCellValueChange(
        cellToUpdate.rowIndex,
        cellToUpdate.columnId,
        cellToUpdate.newValue
      );
      setCellToUpdate(null);
    }
    
    setCurrentlyEditingCell(null);
  }
}, [cellToUpdate, handleCellValueChange]);

  // Track if we're updating from parent data to prevent conflicts with pagination
  const isUpdatingFromParentRef = React.useRef(false);

  const loadData = async (
    isInitial = false,
    startIndex?: number,
    overrideFilter?: {
      whereClauses: SpreadsheetTableWhereClause[];
      andOR?: number;
    }
  ) => {
    if (loadingRef.current) {
      return;
    }
    if (!isInitial && startIndex && requestedStarts.current.has(startIndex)) {
      return;
    }

    const incomingId: number | null =
      typeof props.spreadsheetId === "number" ? props.spreadsheetId : null;

    // Reset state when spreadsheet changes
    if (currentSpreadsheetIdRef.current !== incomingId) {
      currentSpreadsheetIdRef.current = incomingId;
      setData([]);
      setColumns([]);
      setSmartColumns([]);
      setColumnsWidth({});
      setStartAt(0);
      setHasMore(true);
      requestedStarts.current.clear();
      columnModelRef.current = null;
      activeFilterRef.current = undefined;
      setFilterVersion(Date.now());
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      if (isInitial) {
        setIsLoading(true);
        // Clear state for initial load to ensure fresh start
        setData([]);
        setColumns([]);
        setSmartColumns([]);
      }
      if (!isInitial && startIndex) requestedStarts.current.add(startIndex);

      // Determine which scenario we're in
      const isSavedSpreadsheet = props.spreadsheetId != null;
      const isRunOnly =
          !isSavedSpreadsheet && props.runPayload?.columnModel?.smartColumns;
      let currentSmartColumns: SmartColumn[] = [];

      // Load columns and initialize state for initial load
      if (isInitial) {
        if (isSavedSpreadsheet) {
          // Scenarios 1 & 3: Load from saved format using appropriate API
          if (!columnModelRef.current) {
            // Check Redux first - if data exists, use it instead of calling API
            const reduxFormatData = getFormatDataFromRedux(props.spreadsheetId, props.spreadsheetType);
            
            let formatData: any = null;
            
            if (reduxFormatData && reduxFormatData.columnModel) {
              // Use data from Redux - preserve exact structure including andOr fields
              formatData = {
                columnModel: reduxFormatData.columnModel,
                queryModel: reduxFormatData.queryModel ? JSON.parse(JSON.stringify(reduxFormatData.queryModel)) : { whereClauses: [] },
                formatDetails: reduxFormatData.formatDetails
              };
            } else {
              // Data not in Redux - call API
              const loadFormatFn =
                props.spreadsheetType === SpreadsheetType.pedigree
                  ? loadPedigreeSpreadsheetFormat
                  : loadIndividualSpreadsheetFormat;

              formatData = await loadFormatFn(Number(props.spreadsheetId));
              
              // Store in Redux after fetching - preserve exact structure
              if (formatData) {
                // Deep clone to preserve all fields including andOr
                const formatDataToStore = {
                  columnModel: formatData.columnModel || formatData,
                  queryModel: formatData.queryModel ? JSON.parse(JSON.stringify(formatData.queryModel)) : { whereClauses: [] },
                  formatDetails: formatData.formatDetails
                };
                storeFormatDataInRedux(Number(props.spreadsheetId), props.spreadsheetType, formatDataToStore);
              }
            }
            
            if (!formatData) {
              console.warn('No format data available for saved spreadsheet');
              setData([]);
              setHasMore(false);
              return;
            }
            currentSmartColumns = formatData.columnModel?.smartColumns || [];

            const columnModel = formatData.columnModel || formatData;
            let apiSmartColumns = columnModel?.smartColumns || [];
            if (!apiSmartColumns || apiSmartColumns.length === 0) {
              console.warn('No smart columns available for saved spreadsheet');
              setData([]);
              setHasMore(false);
              return;
            }
            // Ensure row count column exists before mapping
            apiSmartColumns = ensureRowCountColumn(apiSmartColumns, props.spreadsheetType);

            const mappedColumns = mapApiColumnsToTableColumns(apiSmartColumns);
            const sortedColumnsWithWidth = applySortingFromApi(mappedColumns, apiSmartColumns);
            setColumns(sortedColumnsWithWidth);
            setSmartColumns(apiSmartColumns);
            columnModelRef.current = { smartColumns: apiSmartColumns };
            formatDetailsRef.current = formatData.formatDetails;
            queryDetailsRef.current = formatData.queryModel || { whereClauses: [] };

            const initialColumnsWidth: Record<SpreadsheetTableColumnId, number> = {};
            initialColumnsWidth[SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID] =
              SPREADSHEET_TABLE_CONSTANTS.MIN_COLUMN_WIDTH;
            apiSmartColumns.forEach((col: SmartColumn) => {
              if (!col.field) {
                return;
              }
              if (col.field.fieldid !== SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID) {
                initialColumnsWidth[col.field.fieldid] =
                  col.width || SPREADSHEET_TABLE_CONSTANTS.MIN_COLUMN_WIDTH;
              }
            });
            setColumnsWidth(initialColumnsWidth);
          }

          // Initialize filter from Redux when available (saved spreadsheet scenario)
          const reduxFilter = getFilterFromRedux(props.spreadsheetId,props.spreadsheetType);
          if (reduxFilter) {
            activeFilterRef.current = reduxFilter;
            setFilterVersion(Date.now());
          }
        } else if (isRunOnly && props.runPayload) {
          // Scenario 2: Load from runPayload
          let smartColumns = props.runPayload.columnModel.smartColumns;
          // Ensure row count column exists before mapping
          smartColumns = ensureRowCountColumn(smartColumns, props.spreadsheetType);
          let mappedColumns = mapRunPayloadColumnsToTableColumns(smartColumns);

          // Apply sorting information from API
          mappedColumns = applySortingFromApi(mappedColumns, smartColumns);

          setColumns(mappedColumns);
          setSmartColumns(smartColumns);
          currentSmartColumns = smartColumns;
          columnModelRef.current = { smartColumns };
          formatDetailsRef.current = props.runPayload.ssFormat;
          queryDetailsRef.current = props.runPayload.queryModel ?? {
            whereClauses: [],
          };

          // Initialize activeFilterRef from runPayload filter (if present) or from Redux
          if ((props.runPayload as any).filter && (props.runPayload as any).filter.whereClauses && (props.runPayload as any).filter.whereClauses.length > 0) {
            // Use filter from run payload if it exists
            activeFilterRef.current = (props.runPayload as any).filter;
            setFilterVersion(Date.now());
          } else if (props.spreadsheetId != null && props.spreadsheetId !== undefined && typeof window !== 'undefined' && (window as any).__SPREADSHEET_STORE__) {
            // Fallback: Try to get filter from Redux
            try {
              const store = (window as any).__SPREADSHEET_STORE__;
              const state = store.getState();
              const spreadsheetType = props.spreadsheetType === SpreadsheetType.individual ? 'Individual' : 'Pedigree';
              const typeData = state?.spreadsheetFormat?.[spreadsheetType] || {};
              const storedData = typeData[Number(props.spreadsheetId)];
              if (storedData && storedData.filter && storedData.filter.whereClauses && storedData.filter.whereClauses.length > 0) {
                activeFilterRef.current = storedData.filter;
                setFilterVersion(Date.now());
              }
            } catch (e) {
              console.warn('Could not retrieve filter from Redux:', e);
            }
          }

          // Initialize column widths (include Sr No column)
          const initialColumnsWidth: Record<SpreadsheetTableColumnId, number> =
            {};
          initialColumnsWidth[SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID] =
            SPREADSHEET_TABLE_CONSTANTS.MIN_COLUMN_WIDTH; // Sr No column width
          smartColumns.forEach((col: SmartColumn) => {
            // Safety check: ensure col.field exists before accessing fieldid
            if (!col.field) {
              return;
            }

            if (
              col.field.fieldid !==
              SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID
            ) {
              initialColumnsWidth[col.field.fieldid] =
                col.width || SPREADSHEET_TABLE_CONSTANTS.MIN_COLUMN_WIDTH;
            }
          });
          setColumnsWidth(initialColumnsWidth);
        } else {
          console.warn("No valid payload provided");
          if (!isSavedSpreadsheet && !isRunOnly) {
            setData([]);
          }
          return;
        }
      }

      // Build models separately: queryModel (saved/run) and filter (UI)
      // Query model comes only from saved format or run payload
      const apiQuery = isRunOnly ? undefined : (queryDetailsRef.current || undefined);
      const runPayloadQuery = isRunOnly && props.runPayload?.queryModel ? props.runPayload.queryModel : undefined;
      const queryModelToUse = computeQueryModel(props.spreadsheetType, {
        apiQuery,
        runPayloadQuery,
      });

      // Filters come only from user actions (active filter or override)
      let filterToUse:
        | { whereClauses: SpreadsheetTableWhereClause[]; andOR?: number }
        | undefined;
      filterToUse = overrideFilter || activeFilterRef.current || undefined;

      if (!filterToUse && isSavedSpreadsheet) {
        const reduxFilter = getFilterFromRedux(props.spreadsheetId,props.spreadsheetType);
        if (reduxFilter) {
          filterToUse = reduxFilter;
          activeFilterRef.current = reduxFilter;
          setFilterVersion(Date.now());
        }
      }

      // Fetch data based on scenario and spreadsheet type
      let dataResult: SpreadsheetTableApiResponse;

      if (isSavedSpreadsheet) {
        // Check if columnModel is available
        if (!columnModelRef.current) {
          console.error("loadData: columnModelRef.current is undefined for saved spreadsheet");
          throw new Error("Column model is not available for data loading");
        }
        
        // Use appropriate API endpoint based on spreadsheet type
        const payload = createDataPayload(columnModelRef.current, {
          startAt: startIndex ?? startAt,
          size: SPREADSHEET_TABLE_CONSTANTS.DEFAULT_PAGE_SIZE,
          scroll: true,
          query: queryModelToUse,
          filter: filterToUse,
          folderId: props.folderId ?? 0,
          familyId: props.familyId ?? 0,
        });
        
        if (props.spreadsheetType === SpreadsheetType.pedigree) {
          dataResult = await fetchPedigreeSpreadsheetData(payload);
        } else {
          dataResult = await fetchSpreadsheetData(payload);
        }
      } else if (isRunOnly) {
        // Use appropriate API endpoint for run-only scenarios
        const runPayloadDataPayload = createRunPayloadDataPayload(
          props.runPayload!,
          {
            startAt: startIndex ?? startAt,
            size: SPREADSHEET_TABLE_CONSTANTS.DEFAULT_PAGE_SIZE,
            scroll: true,
            query: queryModelToUse,
            filter: filterToUse,
          }
        );
        
        if (props.spreadsheetType === SpreadsheetType.pedigree) {
          dataResult = await fetchPedigreeSpreadsheetData(runPayloadDataPayload);
        } else {
          dataResult = await fetchSpreadsheetData(runPayloadDataPayload);
        }
      } else {
        throw new Error("No valid scenario detected");
      }
      
      // Handle updated smartColumns from loadISS response (for import scenarios)
      if (dataResult?.columnModel?.smartColumns && Array.isArray(dataResult.columnModel.smartColumns)) {
        const updatedSmartColumns = dataResult.columnModel.smartColumns;
        
        setSmartColumns(updatedSmartColumns);
        currentSmartColumns = updatedSmartColumns;
        // Update columns with new editable/securityEditable properties
        const mappedColumns = mapApiColumnsToTableColumns(updatedSmartColumns);
        setColumns(mappedColumns);
        
        // Update column model ref
        columnModelRef.current = dataResult.columnModel;
      }

      dataResult.data.forEach(rowArray => {
        rowArray.forEach((cell, index) => {
          const smartColumn = columnModelRef.current.smartColumns[index];

          if (
            smartColumn &&
            smartColumn.parentField &&
            smartColumn.parentField.fieldid &&
            cell &&
            cell.field
          ) {
            cell.tableId = smartColumn.parentField.fieldid;
          }
        });
      });
      
      // Append new results to existing dataResult instead of replacing
      setDataResult((prev) => {
        try {
          if (!prev) return dataResult;

          const extractData = (res: any): any[] => {
            if (Array.isArray(res)) return res;
            if (Array.isArray(res?.data)) return res.data;
            return [];
          };
          const extractRows = (res: any): any[] => {
            if (Array.isArray(res?.rows)) return res.rows;
            return [];
          };

          const mergedData = [...extractData(prev), ...extractData(dataResult)];
          const mergedRows = [...extractRows(prev), ...extractRows(dataResult)];

          // Preserve other fields from latest response when available
          const latestObj = Array.isArray(dataResult) ? {} : dataResult || {};
          const prevObj = Array.isArray(prev) ? {} : prev || {};

          return {
            ...prevObj,
            ...latestObj,
            data: mergedData,
            rows: mergedRows,
          } as SpreadsheetTableApiResponse;
        } catch {
          // Fallback to latest if merge fails
          return dataResult;
        }
      });
      // Process and merge data
      const cellMatrix = normalizeCellMatrix(dataResult);

      // Get fieldContext for API calls
      const fieldContext =
        props.spreadsheetType === SpreadsheetType.individual
          ? 1 // INDIVIDUAL context
          : 2; // PEDIGREE context

      const newRows = mapApiDataToRows(
        cellMatrix,
        startIndex ?? startAt,
        Boolean(isRunOnly),
        dataResult?.rows,
        fieldContext
      );

      setData((prev) => {
        const updatedData = isInitial ? newRows : prev.concat(newRows);
        // Notify parent only for authoritative data loads to reduce re-renders
        if (props.onCurrentDataChange && !isUpdatingFromParentRef.current) {
          try {
            props.onCurrentDataChange(updatedData);
          } catch {}
        }
        return updatedData;
      });

      const loadedNow = (startIndex ?? startAt) + newRows.length;
      setStartAt(loadedNow);

      // Handle pagination and record counts
      if (isInitial) {
        if (isSavedSpreadsheet) {
          // Always prefer API count when available
          const apiCount = Number(dataResult?.count);
          if (isFinite(apiCount) && apiCount > 0) {
            localStorage.setItem(`spreadsheet-count-${props.spreadsheetId}`, apiCount.toString());
            setTotalCount(apiCount);
            const hasMoreValue = loadedNow < apiCount;
            setHasMore(hasMoreValue);
          } else {
            // Use page-based detection as fallback instead of setting hasMore = false. This allows pagination to continue even when API doesn't return a count
            const pageSize = SPREADSHEET_TABLE_CONSTANTS.DEFAULT_PAGE_SIZE;
            setTotalCount(null);
            const hasMoreValue = newRows.length >= pageSize;
            setHasMore(hasMoreValue);
          }
        } else {
          // Run-only scenarios: use page-based detection or unsavedSSCount
          const unsavedCount = localStorage.getItem(
            `spreadsheet-count-${props.spreadsheetId}`
          );
          if (unsavedCount) {
            setTotalCount(parseInt(unsavedCount));
            setHasMore(loadedNow < parseInt(unsavedCount));
          } else {
            // For new spreadsheets without unsavedSSCount, use API response count
            const count = Number(dataResult?.count ?? newRows.length);
            if (isFinite(count)) {
              setTotalCount(count);
              setHasMore(loadedNow < count);
              // Store the count in localStorage for new spreadsheets
              localStorage.setItem(
                `spreadsheet-count-${props.spreadsheetId}`,
                count.toString()
              );
            } else {
              setTotalCount(null);
              setHasMore(
                newRows.length >= SPREADSHEET_TABLE_CONSTANTS.DEFAULT_PAGE_SIZE
              );
            }
          }
        }
      } else {
        if (isSavedSpreadsheet) {
          // Prefer API count if available, fallback to stored count. This ensures fresh counts from API take precedence over stale cache
          const apiCount = Number(dataResult?.count);
          const storedCount = localStorage.getItem(
            `spreadsheet-count-${props.spreadsheetId}`
          );

          let count: number;
          if (isFinite(apiCount) && apiCount > 0) {
            // Update stored count with fresh API data
            localStorage.setItem(`spreadsheet-count-${props.spreadsheetId}`, apiCount.toString());
            count = apiCount;
          } else if (storedCount) {
            count = Number(storedCount);
          } else {
            count = loadedNow;
          }

          setTotalCount(count);
          // Use page-based detection as additional safeguard
          const hasMoreByCount = loadedNow < count;
          const hasMoreByPageSize = newRows.length >= SPREADSHEET_TABLE_CONSTANTS.DEFAULT_PAGE_SIZE;
          const finalHasMore = hasMoreByCount || hasMoreByPageSize;
          setHasMore(finalHasMore);
        } else {
          // For unsaved spreadsheets, check unsavedSSCount
          const unsavedCount = localStorage.getItem(
            `spreadsheet-count-${props.spreadsheetId}`
          );
          if (unsavedCount) {
            const count = parseInt(unsavedCount);
            setTotalCount(count);
            setHasMore(loadedNow < count);
          } else {
            // For new spreadsheets without unsavedSSCount, use page-based detection
            // Also try to get count from API response if available
            const count = Number(dataResult?.count);
            if (isFinite(count) && count > 0) {
              setTotalCount(count);
              setHasMore(loadedNow < count);
              // Store the count in localStorage for new spreadsheets
              localStorage.setItem(
                `spreadsheet-count-${props.spreadsheetId}`,
                count.toString()
              );
            } else {
              setHasMore(
                newRows.length >= SPREADSHEET_TABLE_CONSTANTS.DEFAULT_PAGE_SIZE
              );
            }
          }
        }
      }
    } catch (err) {
      console.error("Error loading spreadsheet data:", err);
      const errorMessage =
        (err as any)?.entity ||
        (err as any)?.message ||
        "Failed to load spreadsheet data";
      if (props.showErrorMessage) {
        props.showErrorMessage(errorMessage);
      }
      if (isInitial) {
        setData([]);
      }
      setHasMore(false);
    } finally {
      loadingRef.current = false;
      setLoading(false);
      if (isInitial) {
        setIsLoading(false);
      }
    }
  };

  React.useEffect(() => {
    const currentId: number =
      typeof props.spreadsheetId === "number" ? props.spreadsheetId : 0;

    // Clear stale localStorage count to force fresh API count
    if (currentId) {
      localStorage.removeItem(`spreadsheet-count-${currentId}`);
    }

    // Clear Redux format data cache to force fresh API fetch
    if (currentId && typeof window !== 'undefined' && (window as any).__SPREADSHEET_STORE__) {
      try {
        const store = (window as any).__SPREADSHEET_STORE__;
        const { clearFormatData } = require('@modules/spreadsheets/actions/spreadsheetFormat');
        const spreadsheetTypeKey = props.spreadsheetType === SpreadsheetType.individual ? 'Individual' : 'Pedigree';
        store.dispatch(clearFormatData(currentId, spreadsheetTypeKey));
      } catch (e) {
        console.warn('[useEffect] Could not clear Redux cache:', e);
      }
    }

    // Reset all state
    setData([]);
    setColumnsWidth({});
    setStartAt(0);
    setHasMore(true);
    setTotalCount(null);
    setLoading(false);
    setIsLoading(true);
    requestedStarts.current.clear();
    columnModelRef.current = null;
    activeFilterRef.current = undefined;
    currentSpreadsheetIdRef.current = currentId;

    // Trigger initial data load
    const timeoutId = setTimeout(() => {
      if (currentSpreadsheetIdRef.current === currentId) {
        loadData(true, 0);
      }
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      loadingRef.current = false;
    };
  }, [props.spreadsheetId, props.runPayload]);

  // Sync table data to parent only when table data changes from its own loading
  // Not when it's updated from parent data
  // Removed continuous parent sync effect to avoid feedback loops and flicker

  // Use parent's data when available (for Add/Delete row operations)
  // Only update if the parent data is different and we're not loading
  React.useEffect(() => {
    if (props.parentData && !loadingRef.current) {
      isUpdatingFromParentRef.current = true;
      setData(props.parentData);
      setStartAt(props.parentData.length);

      // Reset the flag after state update
      setTimeout(() => {
        isUpdatingFromParentRef.current = false;
      }, 50);
    }
  }, [props.parentData]);

  // Cleanup effect when component unmounts
  React.useEffect(() => {
    return () => {
      // Cleanup when component unmounts
      loadingRef.current = false;
      requestedStarts.current.clear();
      columnModelRef.current = null;
      activeFilterRef.current = undefined;
      currentSpreadsheetIdRef.current = null;
    };
  }, []);

  const handleColumnWidthChange = React.useCallback(
    async (
      columnId: SpreadsheetTableColumnId,
      newWidth: number,
      spreadsheetType: string
    ) => {
      // Update UI immediately
      setColumnsWidth((prev) => ({ ...prev, [columnId]: newWidth }));
      const smartCols: SmartColumn[] =
        columnModelRef.current?.smartColumns || [];
      const uiCol = columns.find((c) => String(c.id) === String(columnId));
      const uiFieldName = uiCol?.colName?.trim();
      // Find column in data model
      const idxById = smartCols.findIndex(
        (col: SmartColumn) => String(col.field.fieldid) === String(columnId)
      );
      const idxByName = uiFieldName
        ? smartCols.findIndex(
            (col: SmartColumn) =>
              String(col.field.fieldname)?.trim() === uiFieldName
          )
        : -1;
      const columnIndex = idxById !== -1 ? idxById : idxByName;
      // Skip if not found or is special column
      if (
        columnIndex === -1 ||
        !smartCols[columnIndex] ||
        Number(smartCols[columnIndex]?.field?.fieldid) ===
          SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID
      ) {
        console.warn(
          "Column not found or non-resizable. id:",
          columnId,
          "name:",
          uiFieldName
        );
        return;
      }
      // Update width in data model
      const col = smartCols?.[columnIndex];
      if (!col) return;
      col.width = Math.round(newWidth);
      const updatedSmartCols = smartCols.map(({ columnOrder, ...rest }) => ({
        ...rest,
        columnOrder,
      }));
      // Persist changes via API
      try {
        const context =
          spreadsheetType === SpreadsheetType.individual
            ? SPREADSHEET_TYPE.individual
            : SPREADSHEET_TYPE.pedigree;
        const formatDetails = formatDetailsRef.current;
        const saveFormatPayload: SaveFormatPayload = {
          context,
          ssFormat: {
            id: Number(props.spreadsheetId),
            name: formatDetails?.name,
            folderId: formatDetails?.folderId,
            description: formatDetails?.description || "",
          },
          columnModel: { smartColumns: cleanSmartColumnsForApi(updatedSmartCols as any) as any },
          queryModel: (() => {
            const computed = queryDetailsRef.current
              ? computeQueryModel(spreadsheetType, {
                  apiQuery: queryDetailsRef.current as any,
                  normalizeForSave: true,
                })
              : { whereClauses: [] };
            return cleanQueryModelForApi(computed) as any;
          })(),
        };
        await saveFormat(saveFormatPayload);
        
        // Update Redux store with the updated columnModel after successful API save
        // This ensures width changes are preserved when switching back to management mode
        try {
          const spreadsheetTypeName = spreadsheetType === SpreadsheetType.individual ? 'Individual' : 'Pedigree';
          const updatedColumnModel = { smartColumns: updatedSmartCols };
          dispatch(updateFormatData(
            Number(props.spreadsheetId),
            spreadsheetTypeName,
            { columnModel: updatedColumnModel }
          ));
        } catch (reduxErr) {
          console.warn("Failed to update Redux store with column width:", reduxErr);
        }
      } catch (err) {
        console.error("Failed to save column width:", err);
      }
    },
    [columns, setColumnsWidth, props.spreadsheetId, dispatch]
  );

  const handleLoadDataWithFilter = async (filterPayload: {
    filter: SpreadsheetTableQueryModel;
  }) => {
    try {
      setIsLoading(true);

      // Get the current active filter (existing user filters)
      const currentActiveFilter = activeFilterRef.current;

      // Get the new filter being applied
      const newFilter = filterPayload.filter;

      // Merge the new filter with existing active filter
      let mergedFilter;

      if (
        currentActiveFilter &&
        currentActiveFilter.whereClauses &&
        currentActiveFilter.whereClauses.length > 0
      ) {
        // Get the field IDs from the new filter to identify which columns are being updated
        const newFilterFieldIds = new Set(
          newFilter.whereClauses.map(clause => clause.field?.fieldid).filter(Boolean)
        );

        // Remove existing filters for the same columns and add the new ones
        const existingClauses = currentActiveFilter.whereClauses.filter(
          clause => !newFilterFieldIds.has(clause.field?.fieldid)
        );

        mergedFilter = {
          whereClauses: [
            ...existingClauses,
            ...newFilter.whereClauses,
          ],
          // andOR: newFilter.andOR || currentActiveFilter.andOR,
        };
      } else {
        // If no existing active filters, use the new filter as is
        mergedFilter = newFilter;
      }

      // Store the merged filter as the new active filter
      activeFilterRef.current = mergedFilter;
      setFilterVersion(Date.now());

      // Store filter data in Redux by spreadsheet ID and type
      const spreadsheetId = props.spreadsheetId;
      const spreadsheetType = props.spreadsheetType === SpreadsheetType.individual ? 'Individual' : 'Pedigree';
      if (spreadsheetId != null && spreadsheetId !== undefined && dispatch) {
        try {
          dispatch(storeFilterData(Number(spreadsheetId), spreadsheetType, mergedFilter));
        } catch (e) {
          // Non-blocking: if Redux store is not available, continue without storing
          console.warn('Could not store filter data in Redux:', e);
        }
      }

      // Reset data state
      setData([]);
      setStartAt(0);
      setHasMore(true);
      requestedStarts.current.clear();

      // Load data with merged filter (will be merged with API query in loadData)
      await loadData(false, 0, mergedFilter);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadData = async (metadata: {
    pagination?: { start?: number };
  }) => {
    if (!hasMore) return;
    const { start } = metadata.pagination || {};
    const nextStart = start ?? startAt + 1;
    await loadData(false, nextStart);
  };
  // Clear all filters and reload data
  // Clear specific column filter and reload data
  const handleClearColumnFilter = async (columnId: number) => {
    try {
      setIsLoading(true);

      const currentFilter = activeFilterRef.current;
      if (currentFilter && currentFilter.whereClauses) {
        // Fix: Filter by field.fieldid instead of fieldId since we now use the new format
        const updatedWhereClauses = currentFilter.whereClauses.filter(
          (clause) => clause.field?.fieldid !== columnId
        );
        const updatedFilter = updatedWhereClauses.length === 0
          ? undefined
          : { ...currentFilter, whereClauses: updatedWhereClauses };
        
        activeFilterRef.current = updatedFilter;
        setFilterVersion(Date.now());

        // Store updated filter data in Redux by spreadsheet ID and type
        const spreadsheetId = props.spreadsheetId;
        const spreadsheetType = props.spreadsheetType === SpreadsheetType.individual ? 'Individual' : 'Pedigree';
        if (spreadsheetId != null && spreadsheetId !== undefined && dispatch) {
          try {
            if (updatedFilter) {
              dispatch(storeFilterData(Number(spreadsheetId), spreadsheetType, updatedFilter));
            } else {
              // If filter is cleared, remove it from Redux
              dispatch(storeFilterData(Number(spreadsheetId), spreadsheetType, { whereClauses: [] }));
            }
          } catch (e) {
            // Non-blocking: if Redux store is not available, continue without storing
            console.warn('Could not store filter data in Redux:', e);
          }
        }
      }

      // Reset data state
      setData([]);
      setStartAt(0);
      setHasMore(true);
      requestedStarts.current.clear();

      // Load data with updated filter (will merge with API query automatically)
      await loadData(false, 0, activeFilterRef.current);
    } finally {
      setIsLoading(false);
    }
  };

  // =============================================================================
  // EFFECTS
  // =============================================================================

  React.useEffect(() => {
    const currentId: number =
        typeof props.spreadsheetId === "number" ? props.spreadsheetId : 0;

    // Clear stale localStorage count to force fresh API count. This prevents the table from being stuck at old cached counts
    if (currentId) {
      localStorage.removeItem(`spreadsheet-count-${currentId}`);
    }

    // Reset all state
    setData([]);
    setColumnsWidth({});
    setStartAt(0);
    setHasMore(true);
    setTotalCount(null);
    setLoading(false);
    setIsLoading(true);
    requestedStarts.current.clear();
    columnModelRef.current = null;
    activeFilterRef.current = undefined;
    currentSpreadsheetIdRef.current = currentId;

    // Trigger initial data load
    const timeoutId = setTimeout(() => {
      if (currentSpreadsheetIdRef.current === currentId) {
        loadData(true, 0);
      }
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      loadingRef.current = false;
    };
  }, [props.spreadsheetId, props.runPayload]);

  // =============================================================================
  // RETURN API
  // =============================================================================

  const handleSpreadsheetFocus = React.useCallback(() => {
    setActiveSpreadsheetId(props.spreadsheetId);
  }, [props.spreadsheetId]);

  const handleSpreadsheetClick = React.useCallback(() => {
    setActiveSpreadsheetId(props.spreadsheetId);
  }, [props.spreadsheetId]);

  const returnValue = {
    setColumnsWidth,
    columnsWidth,
    handleLoadData,
    data,
    columns,
    selectionState,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleCellMouseUp,
    isCellSelected,
    handleContextMenu,
    closeContextMenu,
    contextMenu,
    handleCopy,
    handleCut,
    handlePaste,
    handleFillDown,
    handleFillUp,
    handleCellValueChange,
    handleOpenDatasheet,
    handleOpenPedigree,
    handleLoadDataWithFilter,
    handleColumnWidthChange,
    handleClearColumnFilter,
    loading,
    isLoading,
    hasRrn,
    hasParentField,
    canAddRow,
    canDeleteRow,
    hasMore,
    totalCount,
    smartColumns,
    dataResult,
    selectedRowData,
    rowSelectionState,
    pageSize: SPREADSHEET_TABLE_CONSTANTS.DEFAULT_PAGE_SIZE,
    setDataResult,
    queryDetailsRef,
    formatDetailsRef,
    columnModelRef,
    setSmartColumns,
    setColumns,
    loadData,
    selectedColumnIndex,
    activeFilterRef,
    clearSelection: () => {
      setSelectionState(createSelectionState());
      setRowSelectionState(clearRowSelection(rowSelectionState));
      setSelectedRowData([]);
      setHasRrn(false);
      setHasParentField(false);
    },
    cellToUpdate,
    setCellToUpdate,
    handleTableClick,
    isUpdatingValue,
    handleSpreadsheetFocus,
    handleSpreadsheetClick,
    filterVersion,
  };

  return returnValue;
};
